import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  Upload,
  FileText,
  AlertCircle,
  User,
  Building2,
  MapPin,
  DollarSign,
  X,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  Camera,
  Sparkles,
  MessageSquare,
  RefreshCw,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Card } from '../ui/Card'
import { Progress } from '../ui/ProgressBar'
import { cn } from '@/lib/utils'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'
import { documentExtraction } from '../../services/documentExtraction'
import { createEscrowData } from '../../utils/escrowCalculations'
import { dataPersistence } from '../../services/dataPersistence'
import { DocumentSigningModal } from './DocumentSigningModal'
import { EsignDocumentsPanel, type EsignDocumentItem, type EsignParty } from './EsignDocumentsPanel'
import { SaleNeelaExtracting } from './SaleNeelaExtracting'
import { SaleNeelaReveal, type SaleDocumentExtracted } from './SaleNeelaReveal'
import { activityLog } from '../../services/activityLog'
import { wizardApi } from '../../services/api/wizard'
import toast from 'react-hot-toast'
import { useConfirm } from '../../contexts/ConfirmProvider'
import { buildDemo1ExtractedDates, DEMO1_QI_WIRE, DEMO1_ESCROW_FILE_NUMBER, DEMO1_COUNTER_SIGNATURES } from '@open1031/shared/demo/demo1'

const SALE_WIZARD_USE_DB = import.meta.env.VITE_USE_DB_WIZARD !== 'false'
const SALE_WIZARD_DEBOUNCE_MS = 500

interface ExchangerInfo {
  firstName: string
  middleName?: string
  lastName: string
  names?: string
  email: string
  phone: string
  homeAddress: string
  street?: string
  city?: string
  state?: string
  zipCode?: string
  taxIdNumber: string
  beneficialInterest?: string
  taxClassification?: string
  llcTaxClassification?: string
}

interface BuyerInfo {
  names: string // For notice
}

interface SellerInfo {
  names: string // For notice
  acknowledgmentSigner?: string // Person who will sign the Notice to Seller on behalf of the seller entity
}

interface ClosingCompanyInfo {
  name: string
  address: string
  phone: string
  email?: string // Company email
  closerName: string // Closing employee's name
  closerEmail: string
  escrowFileNumber?: string // Title/escrow file number — shown on Closing Instructions
}

interface PropertyInfo {
  addressOrLegalDescription: string
  closingDate?: string
  purchaseAgreementDate?: string
  closeDate?: string
}


type InvestmentType =
  | 'like-kind-real-property'
  | 'dst'
  | 'tic'
  | 'nnn'
  | 'oil-gas-mineral'
  | 'leasehold'
  | 'other'
  | ''

interface IdentificationForm {
  exchangerNames: string
  relinquishedPropertyAddress: string
  idRule: '3-property' | '200-percent' | '95-percent' | ''
  identifiedProperties: string[]
  investmentType?: InvestmentType
}

interface DisbursementForm {
  exchangerNames: string
  amount: string
  wireRecipient: string
  wireRecipientAddress: string
  wireRecipientBankName: string
  wireRecipientBankABA: string
  wireRecipientBankAccount: string
  beneficiaryName: string
  referenceNumber: string
}

interface DocumentUpload {
  name: string
  file: File | null
  uploaded: boolean
  required: boolean
  category: 'documents' | 'irs' | 'property' | 'compliance'
  signed?: boolean
}

interface WizardStep {
  id: number
  title: string
  description: string
  phase: string
  completed: boolean
}

const WIZARD_STEPS: WizardStep[] = [
  // Phase One: Sale
  {
    id: 1,
    title: 'Exchanger Information',
    description: 'Exchanger names, home address, and tax ID',
    phase: 'Initial Setup',
    completed: false
  },
  {
    id: 2,
    title: 'Buyer Information',
    description: 'Buyer names for notice',
    phase: 'Initial Setup',
    completed: false
  },
  {
    id: 3,
    title: 'Closing Company (Sale)',
    description: 'Closing company and closer details',
    phase: 'Initial Setup',
    completed: false
  },
  {
    id: 4,
    title: 'Relinquished Property',
    description: 'Property address or legal description',
    phase: 'Initial Setup',
    completed: false
  },
  {
    id: 6,
    title: 'Identification Form',
    description: 'ID rule and identified properties',
    phase: 'Initial Setup',
    completed: false
  },
  // Phase Two: Purchases
  {
    id: 7,
    title: 'Exchanger Information',
    description: 'Exchanger names',
    phase: 'Replacement Acquisition',
    completed: false
  },
  {
    id: 8,
    title: 'Seller Information',
    description: 'Seller names for notice',
    phase: 'Replacement Acquisition',
    completed: false
  },
  {
    id: 9,
    title: 'Closing Company (Purchase)',
    description: 'Closing company and closer details',
    phase: 'Replacement Acquisition',
    completed: false
  },
  {
    id: 10,
    title: 'Replacement Property',
    description: 'Property address or legal description',
    phase: 'Replacement Acquisition',
    completed: false
  },
  {
    id: 11,
    title: 'Disbursement Form',
    description: 'Wire transfer details',
    phase: 'Replacement Acquisition',
    completed: false
  }
]

type WizardMode = 'initial' | 'exchange' | 'acquisition' | 'phase1' | 'disbursement'
type Phase1Step = 'exchange-info' | 'upload-documents' | 'exchangers' | 'property-details' | 'signatures'

const EXCHANGE_TYPE_OPTIONS = [{
  value: 'Forward exchange',
  title: 'Forward exchange',
  description: 'Sell first, then identify within 45 days and close within 180 days.'
}, {
  value: 'Reverse exchange',
  title: 'Reverse exchange',
  description: 'Buy first, then sell the relinquished property. Typically uses an EAT (Exchange Accommodation Titleholder) to “park” title while you complete the required steps.'
}, {
  value: 'Construction Exchange',
  title: 'Construction Exchange',
  description: 'Build or renovate during the exchange window. Typically uses an EAT (parking entity) so completed improvements can count toward value.'
}, {
  value: 'Improvement Exchange',
  title: 'Improvement Exchange',
  description: 'Improve the replacement property before the deadline; a parking structure (often an EAT) may be used so added value is counted.'
}]

const normalizeExchangeType = (value: unknown): string => {
  if (typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (/^i['’]?m\s+not\s+sure$/i.test(trimmed)) return ''
  return trimmed
}

interface SaleWizardProps {
  onComplete: () => void
  onClose: () => void
  initialData?: any
  mode?: WizardMode // controls which subset of steps are shown
  phase1Step?: Phase1Step // for phase1 mode, which step to show
  onPhase1StepChange?: (step: Phase1Step) => void
  embedded?: boolean
  hideFooter?: boolean
  exposeControls?: (api: {
    goPrev: () => void
    goNext: () => void
    submit: (identificationOverride?: IdentificationForm) => void
    saveProgress: () => void
    canPrev: () => boolean
    canNext: () => boolean
    getPercent: () => number
    getLastSavedAt: () => string | null
    isStepComplete: (step: number) => boolean
    getPhase1StepCompletion: () => Record<Phase1Step, boolean>
  }) => void
}

export const SaleWizard: React.FC<SaleWizardProps> = ({ 
  onComplete, 
  onClose,
  initialData,
  mode,
  phase1Step = 'exchange-info',
  onPhase1StepChange,
  embedded,
  hideFooter,
  exposeControls
}) => {
  const { user } = useAuth()
  const { currentExchange, isLoading: exchangeLoading } = useExchange()
  const confirm = useConfirm()
  
  const [isSalesHubSubmitted, setIsSalesHubSubmitted] = useState(() => 
    localStorage.getItem('sales_hub_submitted') === 'true'
  )
  const [isIdentificationHubSubmitted, setIsIdentificationHubSubmitted] = useState(() => 
    localStorage.getItem('identification_hub_submitted') === 'true'
  )
  const [isPurchaseHubSubmitted, setIsPurchaseHubSubmitted] = useState(() => 
    localStorage.getItem('purchase_hub_submitted') === 'true'
  )
  const [isDisbursementHubSubmitted, setIsDisbursementHubSubmitted] = useState(() => 
    localStorage.getItem('disbursement_hub_submitted') === 'true'
  )
  const [isAcquisitionHubSubmitted, setIsAcquisitionHubSubmitted] = useState(() => 
    localStorage.getItem('acquisition_hub_submitted') === 'true'
  )
  const isReadOnly =
    (mode === 'phase1' && isSalesHubSubmitted) ||
    (mode === 'exchange' && isIdentificationHubSubmitted) ||
    (mode === 'acquisition' && isPurchaseHubSubmitted) ||
    (mode === 'disbursement' && isDisbursementHubSubmitted) ||
    (mode === 'acquisition' && isAcquisitionHubSubmitted)

  const exchangerNamePrefilled = mode === 'exchange' && !!initialData?.identificationForm?.exchangerNames
  const relinquishedAddressPrefilled = mode === 'exchange' && !!initialData?.identificationForm?.relinquishedPropertyAddress

  // Determine which steps are in-scope for this wizard mode
  const allowedSteps = React.useMemo<number[]>(() => {
    if (mode === 'phase1') return [-1, 0, 1, 2, 3] // Sales Hub: exchange-info (-1), upload-documents (0), exchangers (1), property-details (2), signatures (3)
    if (mode === 'initial') return [1, 2, 3, 4] // Sales Hub: Exchanger, Buyer, Closing Company, Relinquished Property
    if (mode === 'exchange') return [6] // Identification Hub: Identification Form
    if (mode === 'acquisition') return [7, 8, 9, 10] // Purchase Hub: Exchanger, Seller, Closing Company, Replacement Property
    if (mode === 'disbursement') return [11] // Disbursement Hub: Disbursement Form
    return WIZARD_STEPS.map(s => s.id)
  }, [mode])

  const [currentStep, setCurrentStep] = useState(allowedSteps[0])
  const [showTaxId, setShowTaxId] = useState<Record<number, boolean>>({})
  const [showTaxIdPhaseOne, setShowTaxIdPhaseOne] = useState(false)
  
  const isDemoUser = user?.email === 'demo1@open1031.ai'
  const createDemoFile = (name: string, sizeInMB: number = 2.5) => {
    const content = new Array(Math.floor(sizeInMB * 1024 * 1024)).fill('x').join('')
    return new File([content], name, { type: 'application/pdf' })
  }
  
  // Check if wizard data exists - if not, don't initialize demo files (after reset)
  const exchangeId = currentExchange?.id || user?.id || 'anon'
  const key1 = `exchange_wizard_v1_${exchangeId}_initial`
  const key2 = `sale_wizard_v1_${exchangeId}_initial`
  const key3 = `sale_wizard_v1_${exchangeId}_phase1`
  const existingWizardData = localStorage.getItem(key3) || localStorage.getItem(key2) || localStorage.getItem(key1)
  const existingWizardParsed = existingWizardData ? (() => {
    try {
      return JSON.parse(existingWizardData)
    } catch {
      return null
    }
  })() : null
  const salesAgreementUploadedFromStorage = !!existingWizardParsed?.salesPurchaseAgreementUploaded
  const titleReportUploadedFromStorage = !!existingWizardParsed?.preliminaryTitleReportUploaded
  const [salesPurchaseAgreement, setSalesPurchaseAgreement] = useState<File | null>(
    (isDemoUser && salesAgreementUploadedFromStorage) ? createDemoFile('Sales_Purchase_Agreement.pdf', 3.2) : null
  )
  const [isInEscrow, setIsInEscrow] = useState(false)
  const [preliminaryTitleReport, setPreliminaryTitleReport] = useState<File | null>(
    (isDemoUser && titleReportUploadedFromStorage) ? createDemoFile('Preliminary_Title_Report.pdf', 1.8) : null
  )
  const [isExtracting, setIsExtracting] = useState(false)
  const [isExtractingTitleReport, setIsExtractingTitleReport] = useState(false)
  const [hasExtractedWithNeela, setHasExtractedWithNeela] = useState(
    () => existingWizardParsed?.hasExtractedWithNeela === true
  )
  const [hasTitleReportExtracted, setHasTitleReportExtracted] = useState(
    () => existingWizardParsed?.hasTitleReportExtracted === true
  )
  type SaleExtractionPhase = 'idle' | 'uploading' | 'extracting' | 'revealed'
  const [saleExtractionPhase, setSaleExtractionPhase] = useState<SaleExtractionPhase>(() => {
    if (existingWizardParsed?.hasExtractedWithNeela === true) return 'revealed'
    if (existingWizardParsed?.salesPurchaseAgreementUploaded === true) return 'idle'
    return 'idle'
  })
  const [saleExtractedData, setSaleExtractedData] = useState<SaleDocumentExtracted | null>(
    () => existingWizardParsed?.saleExtractedData ?? null
  )
  type TitleExtractionPhase = 'idle' | 'extracting' | 'revealed'
  const [titleExtractionPhase, setTitleExtractionPhase] = useState<TitleExtractionPhase>(() => {
    if (existingWizardParsed?.hasTitleReportExtracted === true) return 'revealed'
    return 'idle'
  })
  const [titleExtractedData, setTitleExtractedData] = useState<SaleDocumentExtracted | null>(
    () => existingWizardParsed?.titleExtractedData ?? null
  )
  const hasNeelaProcessed = existingWizardParsed?.hasExtractedWithNeela === true || initialData?.hasExtractedWithNeela === true
  const lockSalesHubPrefill = mode === 'phase1' && isDemoUser && !hasNeelaProcessed
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const titleReportInputRef = useRef<HTMLInputElement>(null)
  const titleReportCameraRef = useRef<HTMLInputElement>(null)
  const stepCompletionRef = useRef<Partial<Record<Phase1Step, boolean>>>({})
  
  // Map phase1Step to step number for rendering
  const getPhase1StepNumber = (step: Phase1Step): number => {
    if (step === 'exchange-info') return -1
    if (step === 'upload-documents') return 0
    if (step === 'exchangers') return 1
    if (step === 'property-details') return 2
    if (step === 'signatures') return 3
    return -1
  }
  
  // Get the actual step to render based on mode
  const getRenderStep = (): number => {
    if (mode === 'phase1') {
      return getPhase1StepNumber(phase1Step)
    }
    return currentStep
  }
  // Phase One: Sale
  // Prepopulate from user profile if available, otherwise use initialData or empty
  const getUserFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`
    }
    return ''
  }
  
  // Helper function to get full name from exchanger info
  const getExchangerFullName = (exchanger: ExchangerInfo): string => {
    if (exchanger.names) return exchanger.names // Legacy support
    const parts = [exchanger.firstName, exchanger.middleName, exchanger.lastName].filter(Boolean)
    return parts.join(' ')
  }
  
  // Helper function to parse full name into firstName, middleName, lastName
  const parseFullName = (fullName: string): { firstName: string; middleName?: string; lastName: string } => {
    if (!fullName.trim()) return { firstName: '', lastName: '' }
    const parts = fullName.trim().split(/\s+/)
    if (parts.length === 1) {
      return { firstName: parts[0] || '', lastName: '' }
    } else if (parts.length === 2) {
      return { firstName: parts[0] || '', lastName: parts[1] || '' }
    } else {
      return { firstName: parts[0] || '', middleName: parts.slice(1, -1).join(' '), lastName: parts[parts.length - 1] || '' }
    }
  }
  
  // Get user name parts
  const getUserNameParts = () => {
    if (user?.firstName && user?.lastName) {
      return {
        firstName: user.firstName,
        middleName: (user as any).middleName || '',
        lastName: user.lastName
      }
    }
    return { firstName: '', middleName: '', lastName: '' }
  }
  
  const getUserHomeAddress = () => {
    if (user?.address) {
      const addr = user.address
      const parts = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean)
      return parts.join(', ')
    }
    const cached = dataPersistence.getAll()
    if (cached.address) {
      const parts = [cached.address.street, cached.address.city, cached.address.state, cached.address.zipCode].filter(Boolean)
      return parts.join(', ')
    }
    return ''
  }

  const getUserAddressComponents = () => {
    if (user?.address) {
      return {
        street: user.address.street || '',
        city: user.address.city || '',
        state: user.address.state || '',
        zipCode: user.address.zipCode || ''
      }
    }
    const cached = dataPersistence.getAll()
    if (cached.address) {
      return {
        street: cached.address.street || '',
        city: cached.address.city || '',
        state: cached.address.state || '',
        zipCode: cached.address.zipCode || ''
      }
    }
    return { street: '', city: '', state: '', zipCode: '' }
  }
  
  const getUserTaxId = () => {
    const cached = dataPersistence.getAll()
    return cached.ssn || ''
  }
  
  const getCachedData = () => {
    const cached = dataPersistence.getAll()
    return {
      firstName: cached.firstName || user?.firstName || '',
      lastName: cached.lastName || user?.lastName || '',
      fullName: cached.firstName && cached.lastName ? `${cached.firstName} ${cached.lastName}` : getUserFullName(),
      homeAddress: getUserHomeAddress(),
      taxId: cached.ssn || '',
      propertyAddress: cached.propertyAddress ? `${cached.propertyAddress.street || ''}, ${cached.propertyAddress.city || ''}, ${cached.propertyAddress.state || ''} ${cached.propertyAddress.zipCode || ''}`.trim() : ''
    }
  }
  
  const cachedData = getCachedData()
  
  const getDemoData = () => {
    if (!isDemoUser) return null
    
    const userAddr = user?.address
    
    return {
      exchanger: {
        names: getUserFullName() || 'John Michael Anderson',
        homeAddress: getUserHomeAddress() || '742 Evergreen Terrace, Los Angeles, CA 90210',
        street: userAddr?.street || '1234 Maple Avenue',
        city: userAddr?.city || 'Los Angeles',
        state: userAddr?.state || 'CA',
        zipCode: userAddr?.zipCode || '90001',
        taxIdNumber: getUserTaxId() || '123-45-6789',
        taxClassification: 'individual',
        beneficialInterest: '100'
      },
      buyer: {
        names: 'Pacific Coast Properties LLC'
      },
      closingCompany: {
        name: 'Great Title 1031',
        address: '123 Admiral Way, Washington DC, 21031',
        phone: '213 103 1103',
        email: 'sarah.johnson@greattitle1031.com',
        closerName: 'Sarah Johnson',
        closerEmail: 'support@greattitle1031.com'
      },
      property: {
        addressOrLegalDescription: '456 Sunset Boulevard, Los Angeles, CA 90028',
        closingDate: buildDemo1ExtractedDates().saleCloseDate,
      }
    }
  }
  
  const demoData = getDemoData()
  
  const [exchangers, setExchangers] = useState<ExchangerInfo[]>(() => {
    if (initialData?.exchangers) {
      // Convert legacy names field to firstName/middleName/lastName if needed
      return initialData.exchangers.map((ex: ExchangerInfo) => {
        if (ex.names && (!ex.firstName || !ex.lastName)) {
          const parsed = parseFullName(ex.names)
          return { ...ex, ...parsed }
        }
        return ex
      })
    }
    
    // Get user name parts for auto-fill
    const userNameParts = getUserNameParts()
    
    // For demo: only fill address fields after Neela extraction
    const hasNeelaProcessed = existingWizardParsed?.hasExtractedWithNeela === true || initialData?.hasExtractedWithNeela === true
    if (isDemoUser && !hasNeelaProcessed) {
      const phone = user?.phone || (dataPersistence.getAll().phone as string) || (user?.email === 'demo1@open1031.ai' ? '2131234567' : '')
      return [{
        firstName: userNameParts.firstName,
        middleName: userNameParts.middleName,
        lastName: userNameParts.lastName,
        names: userNameParts.firstName && userNameParts.lastName ? `${userNameParts.firstName} ${userNameParts.middleName ? userNameParts.middleName + ' ' : ''}${userNameParts.lastName}`.trim() : '',
        email: user?.email || '',
        phone,
        homeAddress: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        taxIdNumber: getUserTaxId(),
        beneficialInterest: '100'
      }]
    }
    
    const userAddressComponents = getUserAddressComponents()
    const demoNameParts = demoData?.exchanger.names ? parseFullName(demoData.exchanger.names) : userNameParts
    
    return [{
      firstName: demoNameParts.firstName || userNameParts.firstName,
      middleName: demoNameParts.middleName || userNameParts.middleName,
      lastName: demoNameParts.lastName || userNameParts.lastName,
      names: demoData?.exchanger.names || cachedData.fullName || (userNameParts.firstName && userNameParts.lastName ? `${userNameParts.firstName} ${userNameParts.middleName ? userNameParts.middleName + ' ' : ''}${userNameParts.lastName}`.trim() : ''),
      email: user?.email || '',
      phone: user?.phone || (dataPersistence.getAll().phone as string) || (user?.email === 'demo1@open1031.ai' ? '2131234567' : ''),
      homeAddress: demoData?.exchanger.homeAddress || cachedData.homeAddress,
      street: demoData?.exchanger.street || userAddressComponents.street,
      city: demoData?.exchanger.city || userAddressComponents.city,
      state: demoData?.exchanger.state || userAddressComponents.state,
      zipCode: demoData?.exchanger.zipCode || userAddressComponents.zipCode,
      taxIdNumber: demoData?.exchanger.taxIdNumber || cachedData.taxId,
      beneficialInterest: demoData?.exchanger.beneficialInterest || '100',
      taxClassification: isDemoUser ? 'individual' : '',
      llcTaxClassification: '',
    }]
  })

  const [exchangerInfoPhaseOne, setExchangerInfoPhaseOne] = useState<ExchangerInfo>(() => {
    if (initialData?.exchangerPhaseOne) {
      const ex = initialData.exchangerPhaseOne
      // Convert legacy names field to firstName/middleName/lastName if needed
      if (ex.names && (!ex.firstName || !ex.lastName)) {
        const parsed = parseFullName(ex.names)
        return { ...ex, ...parsed }
      }
      return ex
    }
    
    // Get user name parts for auto-fill
    const userNameParts = getUserNameParts()
    
    const hasNeelaProcessed = existingWizardParsed?.hasExtractedWithNeela === true || initialData?.hasExtractedWithNeela === true
    if (isDemoUser && !hasNeelaProcessed) {
      const phone = user?.phone || (dataPersistence.getAll().phone as string) || (user?.email === 'demo1@open1031.ai' ? '2131234567' : '')
      return {
        firstName: userNameParts.firstName,
        middleName: userNameParts.middleName,
        lastName: userNameParts.lastName,
        names: userNameParts.firstName && userNameParts.lastName ? `${userNameParts.firstName} ${userNameParts.middleName ? userNameParts.middleName + ' ' : ''}${userNameParts.lastName}`.trim() : '',
        email: user?.email || '',
        phone,
        homeAddress: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        taxIdNumber: getUserTaxId()
      }
    }
    
    const userAddressComponents = getUserAddressComponents()
    const demoNameParts = demoData?.exchanger.names ? parseFullName(demoData.exchanger.names) : userNameParts
    
    return {
      firstName: demoNameParts.firstName || userNameParts.firstName,
      middleName: demoNameParts.middleName || userNameParts.middleName,
      lastName: demoNameParts.lastName || userNameParts.lastName,
      names: demoData?.exchanger.names || cachedData.fullName || (userNameParts.firstName && userNameParts.lastName ? `${userNameParts.firstName} ${userNameParts.middleName ? userNameParts.middleName + ' ' : ''}${userNameParts.lastName}`.trim() : ''),
      email: user?.email || '',
      phone: user?.phone || (dataPersistence.getAll().phone as string) || (user?.email === 'demo1@open1031.ai' ? '2131234567' : ''),
      homeAddress: demoData?.exchanger.homeAddress || cachedData.homeAddress,
      street: demoData?.exchanger.street || userAddressComponents.street,
      city: demoData?.exchanger.city || userAddressComponents.city,
      state: demoData?.exchanger.state || userAddressComponents.state,
      zipCode: demoData?.exchanger.zipCode || userAddressComponents.zipCode,
      taxIdNumber: demoData?.exchanger.taxIdNumber || cachedData.taxId,
      taxClassification: isDemoUser ? 'individual' : '',
      llcTaxClassification: '',
    }
  })

  useEffect(() => {
    if (mode !== 'phase1') return
    if (!isDemoUser) return
    if (!user) return
    const userNameParts = getUserNameParts()
    const userAddressComponents = getUserAddressComponents()
    const fullName = userNameParts.firstName && userNameParts.lastName ? `${userNameParts.firstName} ${userNameParts.middleName ? userNameParts.middleName + ' ' : ''}${userNameParts.lastName}`.trim() : ''
    const homeAddress = getUserHomeAddress()
    const taxIdNumber = getUserTaxId()
    const phone = user.phone || (dataPersistence.getAll().phone as string) || (user.email === 'demo1@open1031.ai' ? '2131234567' : '')
    if (phone) {
      dataPersistence.merge({ phone })
    }
    setExchangers(prev => {
      const first = prev[0]
      if (!first) return prev
      const next = {
        ...first,
        firstName: first.firstName || userNameParts.firstName,
        middleName: first.middleName || userNameParts.middleName,
        lastName: first.lastName || userNameParts.lastName,
        names: first.names || fullName,
        email: first.email || user.email || '',
        phone: first.phone || phone,
        homeAddress: hasExtractedWithNeela ? (first.homeAddress || homeAddress) : first.homeAddress,
        street: hasExtractedWithNeela ? (first.street || userAddressComponents.street) : first.street,
        city: hasExtractedWithNeela ? (first.city || userAddressComponents.city) : first.city,
        state: hasExtractedWithNeela ? (first.state || userAddressComponents.state) : first.state,
        zipCode: hasExtractedWithNeela ? (first.zipCode || userAddressComponents.zipCode) : first.zipCode,
        taxIdNumber: first.taxIdNumber || taxIdNumber,
        beneficialInterest: first.beneficialInterest || '100'
      }
      return [next, ...prev.slice(1)]
    })
    setExchangerInfoPhaseOne(prev => ({
      ...prev,
      firstName: prev.firstName || userNameParts.firstName,
      middleName: prev.middleName || userNameParts.middleName,
      lastName: prev.lastName || userNameParts.lastName,
      names: prev.names || fullName,
      email: prev.email || user.email || '',
      phone: prev.phone || phone,
      homeAddress: hasExtractedWithNeela ? (prev.homeAddress || homeAddress) : prev.homeAddress,
      street: hasExtractedWithNeela ? (prev.street || userAddressComponents.street) : prev.street,
      city: hasExtractedWithNeela ? (prev.city || userAddressComponents.city) : prev.city,
      state: hasExtractedWithNeela ? (prev.state || userAddressComponents.state) : prev.state,
      zipCode: hasExtractedWithNeela ? (prev.zipCode || userAddressComponents.zipCode) : prev.zipCode,
      taxIdNumber: prev.taxIdNumber || taxIdNumber
    }))
  }, [mode, isDemoUser, user, hasExtractedWithNeela])
  
  const [buyerInfo, setBuyerInfo] = useState<BuyerInfo>(initialData?.buyer || {
    names: lockSalesHubPrefill ? '' : (demoData?.buyer.names || '')
  })
  const [exchangeInfoConfirmed, setExchangeInfoConfirmed] = useState(false)
  const [uploadConfirmed, setUploadConfirmed] = useState(false)
  const [exchangersReviewed, setExchangersReviewed] = useState(false)
  const [propertyReviewed, setPropertyReviewed] = useState(false)
  
  const [closingCompanySale, setClosingCompanySale] = useState<ClosingCompanyInfo>(initialData?.closingCompanySale || {
    name: lockSalesHubPrefill ? '' : (demoData?.closingCompany.name || (dataPersistence.getAll().closingCompanySale as any)?.name || ''),
    address: lockSalesHubPrefill ? '' : (demoData?.closingCompany.address || (dataPersistence.getAll().closingCompanySale as any)?.address || ''),
    phone: lockSalesHubPrefill ? '' : (demoData?.closingCompany.phone || (dataPersistence.getAll().closingCompanySale as any)?.phone || ''),
    email: lockSalesHubPrefill ? '' : (demoData?.closingCompany.email || (dataPersistence.getAll().closingCompanySale as any)?.email || ''),
    closerName: lockSalesHubPrefill ? '' : (demoData?.closingCompany.closerName || (dataPersistence.getAll().closingCompanySale as any)?.closerName || ''),
    closerEmail: lockSalesHubPrefill ? '' : (demoData?.closingCompany.closerEmail || (dataPersistence.getAll().closingCompanySale as any)?.closerEmail || '')
  })
  
  const [relinquishedProperty, setRelinquishedProperty] = useState<PropertyInfo>(initialData?.relinquished || {
    addressOrLegalDescription: lockSalesHubPrefill ? '' : (demoData?.property.addressOrLegalDescription || cachedData.propertyAddress),
    closingDate: lockSalesHubPrefill ? '' : (demoData?.property.closingDate || '')
  })
  
  // Exchange info step questions - Base questions (all exchange types)
  const [exchangeType, setExchangeType] = useState<string>(
    normalizeExchangeType(initialData?.exchangeType ?? existingWizardParsed?.exchangeType)
  )
  const [propertySaleStatus, setPropertySaleStatus] = useState<string>(
    initialData?.propertySaleStatus ?? existingWizardParsed?.propertySaleStatus ?? ''
  )
  const [listPrice, setListPrice] = useState<string>(
    initialData?.listPrice ?? existingWizardParsed?.listPrice ?? ''
  )
  const [mortgagesPaidOff, setMortgagesPaidOff] = useState<string>(
    initialData?.mortgagesPaidOff ?? existingWizardParsed?.mortgagesPaidOff ?? ''
  )
  
  // Reverse Exchange questions
  const [reverseReplacementAcquired, setReverseReplacementAcquired] = useState<string>(initialData?.reverseReplacementAcquired || '')
  const [reverseHasTargetProperty, setReverseHasTargetProperty] = useState<string>(initialData?.reverseHasTargetProperty || '')
  const [reverseIntendedPurchaseDate, setReverseIntendedPurchaseDate] = useState<string>(initialData?.reverseIntendedPurchaseDate || '')
  const [reverseTargetPropertyAddress, setReverseTargetPropertyAddress] = useState<string>(initialData?.reverseTargetPropertyAddress || '')
  const [reverseTargetPropertyPrice, setReverseTargetPropertyPrice] = useState<string>(initialData?.reverseTargetPropertyPrice || '')
  const [reverseReplacementFunding, setReverseReplacementFunding] = useState<string>(initialData?.reverseReplacementFunding || '')
  const [reverseAcquisitionDate, setReverseAcquisitionDate] = useState<string>(initialData?.reverseAcquisitionDate || '')
  const [reverseReplacementAddress, setReverseReplacementAddress] = useState<string>(initialData?.reverseReplacementAddress || '')
  const [reverseReplacementSellerName, setReverseReplacementSellerName] = useState<string>(initialData?.reverseReplacementSellerName || '')
  const [reverseReplacementClosingCompany, setReverseReplacementClosingCompany] = useState<string>(initialData?.reverseReplacementClosingCompany || '')
  const [reverseReplacementClosingEmail, setReverseReplacementClosingEmail] = useState<string>(initialData?.reverseReplacementClosingEmail || '')
  const [reverseReplacementClosingPhone, setReverseReplacementClosingPhone] = useState<string>(initialData?.reverseReplacementClosingPhone || '')
  const [reverseReplacementFundingMethod, setReverseReplacementFundingMethod] = useState<string>(initialData?.reverseReplacementFundingMethod || '')
  const [reverseTitleHolder, setReverseTitleHolder] = useState<string>(initialData?.reverseTitleHolder || '')
  const [reverseRelinquishedAddress, setReverseRelinquishedAddress] = useState<string>(initialData?.reverseRelinquishedAddress || '')
  const [reverseRelinquishedDebt, setReverseRelinquishedDebt] = useState<string>(initialData?.reverseRelinquishedDebt || '')
  const [reverseRelinquishedClosingCompany, setReverseRelinquishedClosingCompany] = useState<string>(initialData?.reverseRelinquishedClosingCompany || '')
  const [reverseRelinquishedClosingEmail, setReverseRelinquishedClosingEmail] = useState<string>(initialData?.reverseRelinquishedClosingEmail || '')
  const [reverseRelinquishedClosingPhone, setReverseRelinquishedClosingPhone] = useState<string>(initialData?.reverseRelinquishedClosingPhone || '')
  const [reverseRelinquishedListed, setReverseRelinquishedListed] = useState<string>(initialData?.reverseRelinquishedListed || '')
  const [reverseEATAware, setReverseEATAware] = useState<string>(initialData?.reverseEATAware || '')
  const [reverse180Acknowledgment, setReverse180Acknowledgment] = useState<string>(initialData?.reverse180Acknowledgment || '')
  
  // Improvement/Construction Exchange questions
  const [improvementReplacementClosed, setImprovementReplacementClosed] = useState<string>(initialData?.improvementReplacementClosed || '')
  const [improvementIntendedCloseDate, setImprovementIntendedCloseDate] = useState<string>(initialData?.improvementIntendedCloseDate || '')
  const [improvementTargetAddress, setImprovementTargetAddress] = useState<string>(initialData?.improvementTargetAddress || '')
  const [improvementSalesAmount, setImprovementSalesAmount] = useState<string>(initialData?.improvementSalesAmount || '')
  const [improvementReplacementAddress, setImprovementReplacementAddress] = useState<string>(initialData?.improvementReplacementAddress || '')
  const [improvementSalePrice, setImprovementSalePrice] = useState<string>(initialData?.improvementSalePrice || '')
  const [improvementPropertyStatus, setImprovementPropertyStatus] = useState<string>(initialData?.improvementPropertyStatus || '')
  const [improvementConstructionScope, setImprovementConstructionScope] = useState<string>(initialData?.improvementConstructionScope || '')
  const [improvementConstructionBudget, setImprovementConstructionBudget] = useState<string>(initialData?.improvementConstructionBudget || '')
  const [improvement180DeadlineAware, setImprovement180DeadlineAware] = useState<string>(initialData?.improvement180DeadlineAware || '')
  const [improvementEATAware, setImprovementEATAware] = useState<string>(initialData?.improvementEATAware || '')
  const [improvementContractorSelected, setImprovementContractorSelected] = useState<string>(initialData?.improvementContractorSelected || '')
  const [improvementContractsSigned, setImprovementContractsSigned] = useState<string>(initialData?.improvementContractsSigned || '')
  
  // Refs to store latest question values for saveProgressToStorage (prevents auto-save on question changes)
  const exchangeTypeRef = React.useRef(exchangeType)
  const propertySaleStatusRef = React.useRef(propertySaleStatus)
  const listPriceRef = React.useRef(listPrice)
  const mortgagesPaidOffRef = React.useRef(mortgagesPaidOff)
  const exchangeInfoSnapshotRef = React.useRef({ exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff })
  const exchangeInfoInitializedRef = React.useRef(false)
  
  // Reverse Exchange refs
  const reverseReplacementAcquiredRef = React.useRef(reverseReplacementAcquired)
  const reverseHasTargetPropertyRef = React.useRef(reverseHasTargetProperty)
  const reverseIntendedPurchaseDateRef = React.useRef(reverseIntendedPurchaseDate)
  const reverseTargetPropertyAddressRef = React.useRef(reverseTargetPropertyAddress)
  const reverseTargetPropertyPriceRef = React.useRef(reverseTargetPropertyPrice)
  const reverseReplacementFundingRef = React.useRef(reverseReplacementFunding)
  const reverseAcquisitionDateRef = React.useRef(reverseAcquisitionDate)
  const reverseReplacementAddressRef = React.useRef(reverseReplacementAddress)
  const reverseReplacementSellerNameRef = React.useRef(reverseReplacementSellerName)
  const reverseReplacementClosingCompanyRef = React.useRef(reverseReplacementClosingCompany)
  const reverseReplacementClosingEmailRef = React.useRef(reverseReplacementClosingEmail)
  const reverseReplacementClosingPhoneRef = React.useRef(reverseReplacementClosingPhone)
  const reverseReplacementFundingMethodRef = React.useRef(reverseReplacementFundingMethod)
  const reverseTitleHolderRef = React.useRef(reverseTitleHolder)
  const reverseRelinquishedAddressRef = React.useRef(reverseRelinquishedAddress)
  const reverseRelinquishedDebtRef = React.useRef(reverseRelinquishedDebt)
  const reverseRelinquishedClosingCompanyRef = React.useRef(reverseRelinquishedClosingCompany)
  const reverseRelinquishedClosingEmailRef = React.useRef(reverseRelinquishedClosingEmail)
  const reverseRelinquishedClosingPhoneRef = React.useRef(reverseRelinquishedClosingPhone)
  const reverseRelinquishedListedRef = React.useRef(reverseRelinquishedListed)
  const reverseEATAwareRef = React.useRef(reverseEATAware)
  const reverse180AcknowledgmentRef = React.useRef(reverse180Acknowledgment)
  
  // Improvement/Construction Exchange refs
  const improvementReplacementClosedRef = React.useRef(improvementReplacementClosed)
  const improvementIntendedCloseDateRef = React.useRef(improvementIntendedCloseDate)
  const improvementTargetAddressRef = React.useRef(improvementTargetAddress)
  const improvementSalesAmountRef = React.useRef(improvementSalesAmount)
  const improvementReplacementAddressRef = React.useRef(improvementReplacementAddress)
  const improvementSalePriceRef = React.useRef(improvementSalePrice)
  const improvementPropertyStatusRef = React.useRef(improvementPropertyStatus)
  const improvementConstructionScopeRef = React.useRef(improvementConstructionScope)
  const improvementConstructionBudgetRef = React.useRef(improvementConstructionBudget)
  const improvement180DeadlineAwareRef = React.useRef(improvement180DeadlineAware)
  const improvementEATAwareRef = React.useRef(improvementEATAware)
  const improvementContractorSelectedRef = React.useRef(improvementContractorSelected)
  const improvementContractsSignedRef = React.useRef(improvementContractsSigned)
  
  // Update refs when questions change
  React.useEffect(() => {
    exchangeTypeRef.current = exchangeType
  }, [exchangeType])
  React.useEffect(() => {
    propertySaleStatusRef.current = propertySaleStatus
  }, [propertySaleStatus])
  React.useEffect(() => {
    listPriceRef.current = listPrice
  }, [listPrice])
  React.useEffect(() => {
    if (!listPrice) return
    const numeric = parseInt(listPrice.replace(/[^0-9]/g, ''), 10)
    if (!Number.isFinite(numeric) || numeric <= 0) return
    try {
      const existing = JSON.parse(localStorage.getItem('calculator_results') || '{}')
      const updated = { ...existing, input: { ...(existing.input ?? {}), sellingPrice: numeric } }
      localStorage.setItem('calculator_results', JSON.stringify(updated))
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    } catch { /* best-effort sync */ }
  }, [listPrice])
  React.useEffect(() => {
    mortgagesPaidOffRef.current = mortgagesPaidOff
  }, [mortgagesPaidOff])
  React.useEffect(() => {
    if (!exchangeInfoInitializedRef.current) {
      exchangeInfoSnapshotRef.current = { exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff }
      exchangeInfoInitializedRef.current = true
      return
    }
    const previous = exchangeInfoSnapshotRef.current
    const hasChanged = previous.exchangeType !== exchangeType || previous.propertySaleStatus !== propertySaleStatus || previous.listPrice !== listPrice || previous.mortgagesPaidOff !== mortgagesPaidOff
    if (hasChanged) {
      exchangeInfoSnapshotRef.current = { exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff }
      if (exchangeInfoConfirmed) {
        setExchangeInfoConfirmed(false)
      }
    }
  }, [exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff, exchangeInfoConfirmed])
  
  // Update Reverse Exchange refs
  React.useEffect(() => { reverseReplacementAcquiredRef.current = reverseReplacementAcquired }, [reverseReplacementAcquired])
  React.useEffect(() => { reverseHasTargetPropertyRef.current = reverseHasTargetProperty }, [reverseHasTargetProperty])
  React.useEffect(() => { reverseIntendedPurchaseDateRef.current = reverseIntendedPurchaseDate }, [reverseIntendedPurchaseDate])
  React.useEffect(() => { reverseTargetPropertyAddressRef.current = reverseTargetPropertyAddress }, [reverseTargetPropertyAddress])
  React.useEffect(() => { reverseTargetPropertyPriceRef.current = reverseTargetPropertyPrice }, [reverseTargetPropertyPrice])
  React.useEffect(() => { reverseReplacementFundingRef.current = reverseReplacementFunding }, [reverseReplacementFunding])
  React.useEffect(() => { reverseAcquisitionDateRef.current = reverseAcquisitionDate }, [reverseAcquisitionDate])
  React.useEffect(() => { reverseReplacementAddressRef.current = reverseReplacementAddress }, [reverseReplacementAddress])
  React.useEffect(() => { reverseReplacementSellerNameRef.current = reverseReplacementSellerName }, [reverseReplacementSellerName])
  React.useEffect(() => { reverseReplacementClosingCompanyRef.current = reverseReplacementClosingCompany }, [reverseReplacementClosingCompany])
  React.useEffect(() => { reverseReplacementClosingEmailRef.current = reverseReplacementClosingEmail }, [reverseReplacementClosingEmail])
  React.useEffect(() => { reverseReplacementClosingPhoneRef.current = reverseReplacementClosingPhone }, [reverseReplacementClosingPhone])
  React.useEffect(() => { reverseReplacementFundingMethodRef.current = reverseReplacementFundingMethod }, [reverseReplacementFundingMethod])
  React.useEffect(() => { reverseTitleHolderRef.current = reverseTitleHolder }, [reverseTitleHolder])
  React.useEffect(() => { reverseRelinquishedAddressRef.current = reverseRelinquishedAddress }, [reverseRelinquishedAddress])
  React.useEffect(() => { reverseRelinquishedDebtRef.current = reverseRelinquishedDebt }, [reverseRelinquishedDebt])
  React.useEffect(() => { reverseRelinquishedClosingCompanyRef.current = reverseRelinquishedClosingCompany }, [reverseRelinquishedClosingCompany])
  React.useEffect(() => { reverseRelinquishedClosingEmailRef.current = reverseRelinquishedClosingEmail }, [reverseRelinquishedClosingEmail])
  React.useEffect(() => { reverseRelinquishedClosingPhoneRef.current = reverseRelinquishedClosingPhone }, [reverseRelinquishedClosingPhone])
  React.useEffect(() => { reverseRelinquishedListedRef.current = reverseRelinquishedListed }, [reverseRelinquishedListed])
  React.useEffect(() => { reverseEATAwareRef.current = reverseEATAware }, [reverseEATAware])
  React.useEffect(() => { reverse180AcknowledgmentRef.current = reverse180Acknowledgment }, [reverse180Acknowledgment])
  
  // Update Improvement/Construction Exchange refs
  React.useEffect(() => { improvementReplacementClosedRef.current = improvementReplacementClosed }, [improvementReplacementClosed])
  React.useEffect(() => { improvementIntendedCloseDateRef.current = improvementIntendedCloseDate }, [improvementIntendedCloseDate])
  React.useEffect(() => { improvementTargetAddressRef.current = improvementTargetAddress }, [improvementTargetAddress])
  React.useEffect(() => { improvementSalesAmountRef.current = improvementSalesAmount }, [improvementSalesAmount])
  React.useEffect(() => { improvementReplacementAddressRef.current = improvementReplacementAddress }, [improvementReplacementAddress])
  React.useEffect(() => { improvementSalePriceRef.current = improvementSalePrice }, [improvementSalePrice])
  React.useEffect(() => { improvementPropertyStatusRef.current = improvementPropertyStatus }, [improvementPropertyStatus])
  React.useEffect(() => { improvementConstructionScopeRef.current = improvementConstructionScope }, [improvementConstructionScope])
  React.useEffect(() => { improvementConstructionBudgetRef.current = improvementConstructionBudget }, [improvementConstructionBudget])
  React.useEffect(() => { improvement180DeadlineAwareRef.current = improvement180DeadlineAware }, [improvement180DeadlineAware])
  React.useEffect(() => { improvementEATAwareRef.current = improvementEATAware }, [improvementEATAware])
  React.useEffect(() => { improvementContractorSelectedRef.current = improvementContractorSelected }, [improvementContractorSelected])
  React.useEffect(() => { improvementContractsSignedRef.current = improvementContractsSigned }, [improvementContractsSigned])
  
  const [identificationForm, setIdentificationForm] = useState<IdentificationForm>(() => {
    if (initialData?.identificationForm) {
      return initialData.identificationForm
    }
    
    // Prefill with data from Sales Hub (from initialData)
    const exchangerNames = initialData?.exchangerPhaseOne?.names || ''
    const relinquishedPropertyAddress = initialData?.relinquished?.addressOrLegalDescription || ''
    
    return {
      exchangerNames,
      relinquishedPropertyAddress,
      idRule: '',
      identifiedProperties: [],
      investmentType: '' as InvestmentType,
    }
  })

  // Update identification form with Sales Hub data when it becomes available (only if fields are empty)
  useEffect(() => {
    if (mode === 'exchange') {
      const shouldUpdate = !identificationForm.exchangerNames || !identificationForm.relinquishedPropertyAddress
      
      if (shouldUpdate) {
        const exchangerNames = exchangerInfoPhaseOne?.names || initialData?.exchangerPhaseOne?.names || ''
        const relinquishedPropertyAddress = relinquishedProperty?.addressOrLegalDescription || initialData?.relinquished?.addressOrLegalDescription || ''
        
        if (exchangerNames || relinquishedPropertyAddress) {
          setIdentificationForm(prev => ({
            ...prev,
            exchangerNames: prev.exchangerNames || exchangerNames,
            relinquishedPropertyAddress: prev.relinquishedPropertyAddress || relinquishedPropertyAddress
          }))
        }
      }
    }
  }, [mode, exchangerInfoPhaseOne?.names, relinquishedProperty?.addressOrLegalDescription, initialData?.exchangerPhaseOne?.names, initialData?.relinquished?.addressOrLegalDescription, identificationForm.exchangerNames, identificationForm.relinquishedPropertyAddress])
  
  // Phase Two: Purchases
  const [exchangerInfoPhaseTwo, setExchangerInfoPhaseTwo] = useState<ExchangerInfo>(initialData?.exchangerPhaseTwo || {
    names: getUserFullName(),
    homeAddress: getUserHomeAddress(),
    taxIdNumber: ''
  })
  
  const [sellerInfo, setSellerInfo] = useState<SellerInfo>(initialData?.seller || {
    names: ''
  })
  
  const [closingCompanyPurchase, setClosingCompanyPurchase] = useState<ClosingCompanyInfo>(initialData?.closingCompanyPurchase || {
    name: '',
    address: '',
    phone: '',
    email: '',
    closerName: '',
    closerEmail: ''
  })
  
  const [replacementProperty, setReplacementProperty] = useState<PropertyInfo>(initialData?.replacement || {
    addressOrLegalDescription: ''
  })
  
  const [disbursementForm, setDisbursementForm] = useState<DisbursementForm>(initialData?.disbursementForm || {
    exchangerNames: '',
    amount: '',
    wireRecipient: '',
    wireRecipientAddress: '',
    wireRecipientBankName: '',
    wireRecipientBankABA: '',
    wireRecipientBankAccount: '',
    beneficiaryName: '',
    referenceNumber: '',
  })

  const escrowBalance = React.useMemo<number>(() => {
    if (!user?.id) return 0
    try {
      const raw = localStorage.getItem(`escrow_data_${user.id}`)
      if (!raw) return 0
      const parsed = JSON.parse(raw) as { principalAmount?: number }
      return typeof parsed.principalAmount === 'number' ? parsed.principalAmount : 0
    } catch {
      return 0
    }
  }, [user?.id])

  const [documents, setDocuments] = useState<DocumentUpload[]>(() => {
    // Check if documents are signed from localStorage
    const isAgreementSigned = localStorage.getItem('exchange_agreement_signed') === 'true'
    const isW9Signed = localStorage.getItem('w9_signed') === 'true'
    
    // Base documents - will add more for additional exchangers via useEffect
    return [
    // Initial Setup Documents
    { name: 'Exchange Agreement', file: null, uploaded: isAgreementSigned, required: true, category: 'documents', signed: isAgreementSigned },
    { name: 'Assignment of Relinquished Property Contract', file: null, uploaded: false, required: true, category: 'documents', signed: false },
    { name: 'Notice of Assignment (Buyer)', file: null, uploaded: false, required: true, category: 'documents', signed: false },
    { name: 'W-9 Tax ID Certification', file: null, uploaded: isW9Signed, required: true, category: 'irs', signed: isW9Signed },
    { name: 'Purchase/Sale Agreement (Relinquished)', file: null, uploaded: false, required: true, category: 'property', signed: false },
    { name: 'Preliminary Title Report', file: null, uploaded: false, required: false, category: 'property', signed: false },
    
    // Exchange Period Documents
    { name: 'Settlement Statement (Relinquished - HUD-1)', file: null, uploaded: false, required: true, category: 'documents', signed: false },
    { name: 'Identification of Replacement Property Form', file: null, uploaded: false, required: true, category: 'compliance', signed: false },
    
    // Replacement Acquisition Documents
    { name: 'Purchase Agreement (Replacement)', file: null, uploaded: false, required: true, category: 'property', signed: false },
    { name: 'Assignment of Replacement Property Contract', file: null, uploaded: false, required: true, category: 'documents', signed: false },
    { name: 'Notice of Assignment (Seller)', file: null, uploaded: false, required: true, category: 'documents', signed: false },
    { name: 'Settlement Statement (Replacement - HUD-1)', file: null, uploaded: false, required: true, category: 'documents', signed: false },
    
    // Finalization Documents
    { name: 'Exchange Closing Statement (Relinquished)', file: null, uploaded: false, required: true, category: 'irs', signed: false },
    { name: 'Exchange Closing Statement (Replacement)', file: null, uploaded: false, required: true, category: 'irs', signed: false },
    { name: 'Exchange Transaction Report', file: null, uploaded: false, required: true, category: 'irs', signed: false },
    { name: 'IRS Form 8824 (Like-Kind Exchanges)', file: null, uploaded: false, required: true, category: 'irs', signed: false }
    ]
  })

  const [signingDoc, setSigningDoc] = useState<string | null>(null)
  const [signingDocType, setSigningDocType] = useState<'exchange-agreement' | 'w9' | null>(null)
  const [viewingDoc, setViewingDoc] = useState<string | null>(null)
  const [viewingDocType, setViewingDocType] = useState<'exchange-agreement' | 'w9' | null>(null)
  const [showTicketModal, setShowTicketModal] = useState(false)
  const [ticketMessage, setTicketMessage] = useState('')

  const salesBundleMeta = useMemo(() => {
    const open1031FileNumber = currentExchange?.id
      ? `OPEN1031-${currentExchange.id.replace(/-/g, '').slice(0, 8).toUpperCase()}`
      : undefined
    const exchangeAgreementDate = currentExchange?.createdAt
      ? new Date(currentExchange.createdAt).toISOString().slice(0, 10)
      : undefined
    return {
      open1031FileNumber,
      exchangeAgreementDate,
      exchangeOfficer: DEMO1_COUNTER_SIGNATURES.qiOfficerName,
      settlementAgent: closingCompanySale?.name || undefined,
      escrowFileNumber: closingCompanySale?.escrowFileNumber || DEMO1_ESCROW_FILE_NUMBER,
      intermediaryOfficerName: DEMO1_COUNTER_SIGNATURES.qiOfficerName,
      intermediaryOfficerTitle: DEMO1_COUNTER_SIGNATURES.qiOfficerTitle,
    }
  }, [currentExchange?.id, currentExchange?.createdAt, closingCompanySale?.name, closingCompanySale?.escrowFileNumber])

  const salesEscrowWireData = useMemo(() => ({
    ...DEMO1_QI_WIRE,
    fileNumber: closingCompanySale?.escrowFileNumber || DEMO1_ESCROW_FILE_NUMBER,
  }), [closingCompanySale?.escrowFileNumber])

  const handleSignComplete = (docName: string, signature: string) => {
    setDocuments(prev => {
      const updated = prev.map(doc => 
        doc.name === docName
          ? { ...doc, uploaded: true, signed: true }
          : doc
      )
      return updated
    })
    const signedDate = new Date().toISOString()
    const signedBy = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'
    if (docName === 'Exchange Agreement') {
      localStorage.setItem('exchange_agreement_signed', 'true')
      localStorage.setItem('exchange_agreement_signature', signature)
      localStorage.setItem('exchange_agreement_signed_date', signedDate)
      localStorage.setItem('exchange_agreement_signed_by', signedBy)
      // Dual-write to DB
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      wizardApi.updateHubStatus(exchangeId, {
        documentSignatures: { exchange_agreement: { signed: true, date: signedDate, by: signedBy } },
      }).catch(err => console.error('Failed to sync exchange agreement signature to DB:', err))
    } else if (docName === 'W-9 Tax ID Certification') {
      localStorage.setItem('w9_signed', 'true')
      localStorage.setItem('w9_signature', signature)
      localStorage.setItem('w9_signed_date', signedDate)
      localStorage.setItem('w9_signed_by', signedBy)
      // Dual-write to DB
      const exchangeId = currentExchange?.id || user?.id || 'anon'
      wizardApi.updateHubStatus(exchangeId, {
        documentSignatures: { w9: { signed: true, date: signedDate, by: signedBy } },
      }).catch(err => console.error('Failed to sync W-9 signature to DB:', err))
    }
    activityLog.logDocumentSigned(docName)
    
    setTimeout(() => {
      saveProgressToStorage()
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    }, 100)
    
    setSigningDoc(null)
    setSigningDocType(null)
    toast.success(`${docName} signed successfully!`)
  }

  const handleSubmitTicket = () => {
    if (!ticketMessage.trim()) {
      toast.error('Please enter a description of the changes needed')
      return
    }
    
    const ticketNumber = `TICKET-${Date.now().toString().slice(-6)}`
    
    toast.success(`Edit request submitted! Ticket #${ticketNumber}`)
    
    const tickets = JSON.parse(localStorage.getItem('edit_tickets') || '[]')
    tickets.push({
      ticketNumber,
      message: ticketMessage,
      status: 'pending',
      section: 'sales',
      submittedAt: new Date().toISOString()
    })
    localStorage.setItem('edit_tickets', JSON.stringify(tickets))
    
    setTicketMessage('')
    setShowTicketModal(false)
  }

  const isStepComplete = (step: number): boolean => {
    // Phase 1 mode completion checks
    if (mode === 'phase1') {
      const hasRequiredDoc = !!salesPurchaseAgreement
      const hasTitleReportIfNeeded = !isInEscrow || !!preliminaryTitleReport
      const hasTitleExtractionIfPresent = !preliminaryTitleReport || hasTitleReportExtracted
      const documentsReadyForConfirm = hasRequiredDoc && hasTitleReportIfNeeded && hasExtractedWithNeela && hasTitleExtractionIfPresent
      const bothDocsUploaded = documentsReadyForConfirm && uploadConfirmed
      if (step === 0) { // Upload documents step
        return documentsReadyForConfirm && uploadConfirmed
      }
      if (step === 1) { // Exchangers step
        const allBasicFieldsComplete = exchangers.length > 0 && exchangers.every(e => {
          const hasAddress = !!(e.homeAddress || (e.street && e.city && e.state && e.zipCode))
          const hasClassification = !!(e.taxClassification && (e.taxClassification !== 'llc' || e.llcTaxClassification))
          return !!(e.firstName && e.lastName && e.email && e.phone && hasAddress && e.taxIdNumber && hasClassification)
        })
        const allPercentagesComplete = exchangers.every(e => {
          const percentage = e.beneficialInterest || (exchangers.length === 1 ? '100' : '')
          return percentage && parseFloat(percentage) > 0 && parseFloat(percentage) <= 100
        })
        const reviewConfirmed = !isDemoUser || !hasExtractedWithNeela || exchangersReviewed
        return bothDocsUploaded && allBasicFieldsComplete && allPercentagesComplete && reviewConfirmed
      }
      if (step === 2) { // Property details step
        const reviewConfirmed = !isDemoUser || !hasExtractedWithNeela || propertyReviewed
        return bothDocsUploaded && !!(relinquishedProperty.addressOrLegalDescription && buyerInfo.names && closingCompanySale.name && closingCompanySale.address && closingCompanySale.phone && closingCompanySale.closerName && closingCompanySale.closerEmail) && reviewConfirmed
      }
      if (step === 3) { // Signatures step
        // Check that all exchangers have signed their Exchange Agreement and W-9
        const allExchangersSigned = exchangers.every((exchanger, index) => {
          const exchangerName = exchanger.names || `Exchanger ${index + 1}`
          const exchangeAgreementName = index === 0 
            ? 'Exchange Agreement' 
            : `Exchange Agreement (${exchangerName})`
          const w9Name = index === 0
            ? 'W-9 Tax ID Certification'
            : `W-9 Tax ID Certification (${exchangerName})`
          
          const exchangeAgreement = documents.find(d => d.name === exchangeAgreementName)
          const w9 = documents.find(d => d.name === w9Name)
          
          return !!(exchangeAgreement?.signed && w9?.signed)
        })
        return allExchangersSigned
      }
    }
    
    switch(step) {
      case 1: // Exchanger Information (Phase One)
        if (mode === 'phase1') {
          // For phase1 mode, check exchangers array - require firstName, lastName, email, phone, address, taxId, and beneficial interest
          return exchangers.length > 0 && exchangers.every(e => 
            e.firstName && e.lastName && e.email && e.phone && (e.homeAddress || (e.street && e.city && e.state && e.zipCode)) && e.taxIdNumber && 
            e.beneficialInterest && parseFloat(e.beneficialInterest) > 0
          )
        }
        return !!(exchangerInfoPhaseOne.firstName && exchangerInfoPhaseOne.lastName && exchangerInfoPhaseOne.email && exchangerInfoPhaseOne.phone && exchangerInfoPhaseOne.homeAddress && exchangerInfoPhaseOne.taxIdNumber)
      case 2: // Buyer Information
        return !!buyerInfo.names
      case 3: // Closing Company (Sale)
        return !!(closingCompanySale.name && closingCompanySale.address && closingCompanySale.phone && closingCompanySale.closerName && closingCompanySale.closerEmail)
      case 4: // Relinquished Property
        return !!relinquishedProperty.addressOrLegalDescription
      case 6: // Identification Form
        return !!(identificationForm.exchangerNames && identificationForm.relinquishedPropertyAddress && identificationForm.idRule && identificationForm.identifiedProperties.length > 0)
      case 7: // Exchanger Information (Phase Two)
        return !!exchangerInfoPhaseTwo.names
      case 8: // Seller Information
        return !!sellerInfo.names
      case 9: // Closing Company (Purchase)
        return !!(closingCompanyPurchase.name && closingCompanyPurchase.address && closingCompanyPurchase.closerName && closingCompanyPurchase.closerEmail)
      case 10: // Replacement Property
        return !!replacementProperty.addressOrLegalDescription
      case 11: // Disbursement Form
        return !!(disbursementForm.exchangerNames && disbursementForm.amount && disbursementForm.wireRecipient && disbursementForm.wireRecipientAddress && disbursementForm.beneficiaryName && disbursementForm.wireRecipientBankName && disbursementForm.wireRecipientBankABA && disbursementForm.wireRecipientBankAccount)
      default:
        return false
    }
  }

  const getNextStepGuidance = (): { message: string; actionNeeded: string } | null => {
    if (mode !== 'phase1' || !isDemoUser) return null
    if (phase1Step === 'exchange-info') {
      const isComplete = !!(exchangeType && propertySaleStatus && listPrice && mortgagesPaidOff)
      if (!isComplete) {
        return { message: 'Answer all 4 questions to continue', actionNeeded: 'Complete this step' }
      }
      return { message: 'Great! Now upload your documents', actionNeeded: 'Click "Document Upload" above' }
    } else if (phase1Step === 'upload-documents') {
      if (!salesPurchaseAgreement) {
        return { message: 'Upload your Sales & Purchase Agreement', actionNeeded: 'Upload document below' }
      }
      if (!hasExtractedWithNeela) {
        return { message: 'Extract data from your document', actionNeeded: 'Click "Extract with Neela AI"' }
      }
      if (isInEscrow && !preliminaryTitleReport) {
        return { message: 'Upload your Preliminary Title Report', actionNeeded: 'Upload document below' }
      }
      if (preliminaryTitleReport && !hasTitleReportExtracted) {
        return { message: 'Extract data from title report', actionNeeded: 'Click "Extract with Neela AI"' }
      }
      return { message: 'Perfect! Now verify your information', actionNeeded: 'Click "Exchanger Information" above' }
    } else if (phase1Step === 'exchangers') {
      const nonEmptyExchangers = exchangers.filter((e) => !!(e.firstName || e.lastName || e.email || e.phone))
      if (nonEmptyExchangers.length === 0 || !nonEmptyExchangers.every((e) => e.firstName && e.lastName && e.email && e.phone)) {
        return { message: 'Verify all exchanger information is correct', actionNeeded: 'Complete all required fields' }
      }
      return { message: 'Looking good! Verify property details next', actionNeeded: 'Click "Property & Buyer Details" above' }
    } else if (phase1Step === 'property-details') {
      const hasBuyerInfo = !!(buyerInfo.names)
      const hasRelinquishedProperty = !!(relinquishedProperty.addressOrLegalDescription && relinquishedProperty.closingDate)
      const hasClosingCompany = !!(closingCompanySale.name && closingCompanySale.address)
      if (!hasBuyerInfo || !hasRelinquishedProperty || !hasClosingCompany) {
        return { message: 'Verify property and buyer information', actionNeeded: 'Complete all required fields' }
      }
      return { message: 'Almost done! Sign your documents', actionNeeded: 'Click "E-Signatures" above' }
    } else if (phase1Step === 'signatures') {
      const exchangeAgreementDoc = documents.find(d => d.name === 'Exchange Agreement')
      const w9Doc = documents.find(d => d.name === 'W-9 Tax ID Certification')
      const bothSigned = exchangeAgreementDoc?.signed && w9Doc?.signed
      if (!bothSigned) {
        return { message: 'Sign both documents to complete the Sales Hub', actionNeeded: 'E-sign documents below' }
      }
      return { message: 'All done! Submit to finalize', actionNeeded: 'Click "Submit" button' }
    }
    return null
  }

  const renderStepContent = () => {
    const renderStep = getRenderStep()
    const guidance = getNextStepGuidance()
    
    // Phase 1 mode - custom steps
    if (mode === 'phase1') {
      if (phase1Step === 'exchange-info') {
        const exchangeInfoReady = !!(exchangeType && propertySaleStatus && listPrice && mortgagesPaidOff)
        return (
          <div className="flex-1 overflow-y-scroll pr-2 min-h-0" style={{ maxHeight: '100%' }}>
            {guidance && !isReadOnly && (
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{guidance.message}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">{guidance.actionNeeded}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-8">
              {/* Question 1: Exchange Type */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-foreground">What type of exchange are you considering?</h3>
                <div className="space-y-3">
                  {EXCHANGE_TYPE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => !isReadOnly && setExchangeType(option.value)}
                      disabled={isReadOnly}
                      className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                        exchangeType === option.value
                          ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                          : 'border-border bg-card hover:border-green-500'
                      } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="space-y-1">
                        <span className={`font-medium ${
                          exchangeType === option.value
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-green-700 dark:text-green-400'
                        }`}>
                          {option.title}
                        </span>
                        <div className={`text-sm ${
                          exchangeType === option.value
                            ? 'text-foreground/80'
                            : 'text-muted-foreground'
                        }`}>
                          {option.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {(exchangeType === 'Reverse exchange' || exchangeType === 'Construction Exchange' || exchangeType === 'Improvement Exchange') && (
                  <div className="rounded-lg border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent px-4 py-3">
                    <div className="text-sm font-semibold text-foreground">What is an EAT?</div>
                    <div className="mt-1 text-sm text-foreground/80 leading-relaxed">
                      An Exchange Accommodation Titleholder (EAT) is a “parking” entity that temporarily holds title in certain reverse and improvement/construction structures so you can complete the sale, purchase, or improvements within the exchange deadlines.
                    </div>
                  </div>
                )}
              </div>

              {/* Base questions - shown for ALL exchange types */}
              {/* Question 2: Property Sale Status */}
              <div className="space-y-4 border-t border-border pt-6">
                <h3 className="text-xl font-bold text-foreground">What's the status of the property sale?</h3>
                <div className="space-y-3">
                  {['Not yet listed', 'Listed for sale', 'Under contract', 'Sold'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        if (isReadOnly) return
                        setPropertySaleStatus(option)
                        if (option === 'Sold') {
                          localStorage.setItem('propertySaleDate', new Date().toISOString())
                          window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
                        }
                      }}
                      disabled={isReadOnly}
                      className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                        propertySaleStatus === option
                          ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                          : 'border-border bg-card hover:border-green-500'
                      } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className={`font-medium ${
                        propertySaleStatus === option
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-green-700 dark:text-green-400'
                      }`}>
                        {option}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Question 3: Sales Price */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-foreground">What is the sales price of the property?</h3>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/80 font-medium">$</span>
                  <Input
                    type="text"
                    value={listPrice}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9,.]/g, '')
                      setListPrice(value)
                    }}
                    placeholder="0.00"
                    disabled={isReadOnly}
                    className="pl-8 text-lg"
                  />
                </div>
              </div>

              {/* Question 4: Mortgages Paid Off */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-foreground">Will any mortgages be paid off at close?</h3>
                <div className="space-y-3">
                  {['Yes', 'No'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => !isReadOnly && setMortgagesPaidOff(option)}
                      disabled={isReadOnly}
                      className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                        mortgagesPaidOff === option
                          ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                          : 'border-border bg-card hover:border-green-500'
                      } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <span className={`font-medium ${
                        mortgagesPaidOff === option
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-green-700 dark:text-green-400'
                      }`}>
                        {option}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional questions based on exchange type */}
              {exchangeType === 'Reverse exchange' && (
                <>
                  {/* Reverse Exchange - Replacement Property Section */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Replacement Property</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Have you already acquired the replacement property?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setReverseReplacementAcquired(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              reverseReplacementAcquired === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              reverseReplacementAcquired === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {reverseReplacementAcquired === 'No' && (
                      <>
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Do you have a target replacement property?</h4>
                          <div className="space-y-3">
                            {['Yes', 'No'].map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => !isReadOnly && setReverseHasTargetProperty(option)}
                                disabled={isReadOnly}
                                className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                                  reverseHasTargetProperty === option
                                    ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                    : 'border-border bg-card hover:border-green-500'
                                } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <span className={`font-medium ${
                                  reverseHasTargetProperty === option
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-green-700 dark:text-green-400'
                                }`}>
                                  {option}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {reverseHasTargetProperty === 'Yes' && (
                          <>
                            <div className="space-y-4">
                              <h4 className="text-lg font-semibold text-foreground">When do you intend to purchase it?</h4>
                              <Input
                                type="date"
                                value={reverseIntendedPurchaseDate}
                                onChange={(e) => !isReadOnly && setReverseIntendedPurchaseDate(e.target.value)}
                                disabled={isReadOnly}
                                className="w-full"
                              />
                            </div>

                            <div className="space-y-4">
                              <h4 className="text-lg font-semibold text-foreground">What is the address?</h4>
                              <Input
                                type="text"
                                value={reverseTargetPropertyAddress}
                                onChange={(e) => !isReadOnly && setReverseTargetPropertyAddress(e.target.value)}
                                placeholder="Enter property address"
                                disabled={isReadOnly}
                                className="w-full"
                              />
                            </div>

                            <div className="space-y-4">
                              <h4 className="text-lg font-semibold text-foreground">What is the asking price?</h4>
                              <div className="relative">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/80 font-medium">$</span>
                                <Input
                                  type="text"
                                  value={reverseTargetPropertyPrice}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9,.]/g, '')
                                    !isReadOnly && setReverseTargetPropertyPrice(value)
                                  }}
                                  placeholder="0.00"
                                  disabled={isReadOnly}
                                  className="pl-8 w-full"
                                />
                              </div>
                            </div>

                            <div className="space-y-4">
                              <h4 className="text-lg font-semibold text-foreground">How will you fund the replacement property purchase?</h4>
                              <div className="space-y-3">
                                {['Cash', 'Bridge loan', 'Traditional financing'].map((option) => (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => !isReadOnly && setReverseReplacementFunding(option)}
                                    disabled={isReadOnly}
                                    className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                                      reverseReplacementFunding === option
                                        ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                        : 'border-border bg-card hover:border-green-500'
                                    } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                  >
                                    <span className={`font-medium ${
                                      reverseReplacementFunding === option
                                        ? 'text-green-700 dark:text-green-300'
                                        : 'text-green-700 dark:text-green-400'
                                    }`}>
                                      {option}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}

                    {reverseReplacementAcquired === 'Yes' && (
                      <>
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">What was the acquisition date?</h4>
                          <Input
                            type="date"
                            value={reverseAcquisitionDate}
                            onChange={(e) => !isReadOnly && setReverseAcquisitionDate(e.target.value)}
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Address of replacement property</h4>
                          <Input
                            type="text"
                            value={reverseReplacementAddress}
                            onChange={(e) => !isReadOnly && setReverseReplacementAddress(e.target.value)}
                            placeholder="Enter property address"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Seller's name</h4>
                          <Input
                            type="text"
                            value={reverseReplacementSellerName}
                            onChange={(e) => !isReadOnly && setReverseReplacementSellerName(e.target.value)}
                            placeholder="Enter seller name"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Closing company name</h4>
                          <Input
                            type="text"
                            value={reverseReplacementClosingCompany}
                            onChange={(e) => !isReadOnly && setReverseReplacementClosingCompany(e.target.value)}
                            placeholder="Enter closing company name"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Closing company email</h4>
                          <Input
                            type="email"
                            value={reverseReplacementClosingEmail}
                            onChange={(e) => !isReadOnly && setReverseReplacementClosingEmail(e.target.value)}
                            placeholder="Enter closing company email"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Closing company phone</h4>
                          <Input
                            type="tel"
                            value={reverseReplacementClosingPhone}
                            onChange={(e) => !isReadOnly && setReverseReplacementClosingPhone(e.target.value)}
                            placeholder="Enter closing company phone"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">How was the replacement property funded?</h4>
                          <div className="space-y-3">
                            {['Cash', 'Bridge loan', 'Traditional financing'].map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => !isReadOnly && setReverseReplacementFundingMethod(option)}
                                disabled={isReadOnly}
                                className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                                  reverseReplacementFundingMethod === option
                                    ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                    : 'border-border bg-card hover:border-green-500'
                                } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <span className={`font-medium ${
                                  reverseReplacementFundingMethod === option
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-green-700 dark:text-green-400'
                                }`}>
                                  {option}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Who currently holds title to the replacement property?</h4>
                          <div className="space-y-3">
                            {['You', 'An entity created for the exchange', 'Not sure'].map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => !isReadOnly && setReverseTitleHolder(option)}
                                disabled={isReadOnly}
                                className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                                  reverseTitleHolder === option
                                    ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                    : 'border-border bg-card hover:border-green-500'
                                } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <span className={`font-medium ${
                                  reverseTitleHolder === option
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-green-700 dark:text-green-400'
                                }`}>
                                  {option}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Reverse Exchange - Relinquished Property Section */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Relinquished Property</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Relinquished property address</h4>
                      <Input
                        type="text"
                        value={reverseRelinquishedAddress}
                        onChange={(e) => !isReadOnly && setReverseRelinquishedAddress(e.target.value)}
                        placeholder="Enter relinquished property address"
                        disabled={isReadOnly}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Debt amount</h4>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/80 font-medium">$</span>
                        <Input
                          type="text"
                          value={reverseRelinquishedDebt}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9,.]/g, '')
                            !isReadOnly && setReverseRelinquishedDebt(value)
                          }}
                          placeholder="0.00"
                          disabled={isReadOnly}
                          className="pl-8 w-full"
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Closing company name</h4>
                      <Input
                        type="text"
                        value={reverseRelinquishedClosingCompany}
                        onChange={(e) => !isReadOnly && setReverseRelinquishedClosingCompany(e.target.value)}
                        placeholder="Enter closing company name"
                        disabled={isReadOnly}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Closing company email</h4>
                      <Input
                        type="email"
                        value={reverseRelinquishedClosingEmail}
                        onChange={(e) => !isReadOnly && setReverseRelinquishedClosingEmail(e.target.value)}
                        placeholder="Enter closing company email"
                        disabled={isReadOnly}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Closing company phone</h4>
                      <Input
                        type="tel"
                        value={reverseRelinquishedClosingPhone}
                        onChange={(e) => !isReadOnly && setReverseRelinquishedClosingPhone(e.target.value)}
                        placeholder="Enter closing company phone"
                        disabled={isReadOnly}
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Is the relinquished property already listed for sale?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setReverseRelinquishedListed(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              reverseRelinquishedListed === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              reverseRelinquishedListed === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Reverse Exchange - Parking Structure Section */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Parking Structure</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Are you aware that a reverse exchange requires an Exchange Accommodation Titleholder (EAT)?</h4>
                      <p className="text-sm text-muted-foreground">(Informational acknowledgment)</p>
                      <div className="space-y-3">
                        {['Yes', 'No', 'What is this?'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setReverseEATAware(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              reverseEATAware === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              reverseEATAware === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                      {reverseEATAware === 'What is this?' && (
                        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                          <div className="text-sm font-semibold text-foreground">What is an EAT?</div>
                          <div className="mt-1 text-sm text-foreground/80">
                            An Exchange Accommodation Titleholder (EAT) is an entity used to “park” title in a reverse exchange. The EAT temporarily holds legal title to either the replacement or relinquished property while you complete the required sale/purchase steps.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reverse Exchange - 180 Confirmation */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">180-Day Confirmation</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Do you acknowledge that the relinquished property must be sold within 180 days of acquiring the replacement property?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setReverse180Acknowledgment(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              reverse180Acknowledgment === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              reverse180Acknowledgment === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {(exchangeType === 'Construction Exchange' || exchangeType === 'Improvement Exchange') && (
                <>
                  {/* Improvement/Construction Exchange - Replacement Property Status */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Replacement Property Status</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Have you already closed on the replacement property?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setImprovementReplacementClosed(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              improvementReplacementClosed === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              improvementReplacementClosed === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {improvementReplacementClosed === 'No' && (
                      <>
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">When do you intend to close on the property?</h4>
                          <Input
                            type="date"
                            value={improvementIntendedCloseDate}
                            onChange={(e) => !isReadOnly && setImprovementIntendedCloseDate(e.target.value)}
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">If you have a target property, what is the address?</h4>
                          <Input
                            type="text"
                            value={improvementTargetAddress}
                            onChange={(e) => !isReadOnly && setImprovementTargetAddress(e.target.value)}
                            placeholder="Enter property address"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Sales amount</h4>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/80 font-medium">$</span>
                            <Input
                              type="text"
                              value={improvementSalesAmount}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9,.]/g, '')
                                !isReadOnly && setImprovementSalesAmount(value)
                              }}
                              placeholder="0.00"
                              disabled={isReadOnly}
                              className="pl-8 w-full"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {improvementReplacementClosed === 'Yes' && (
                      <>
                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">What is the address of the property?</h4>
                          <Input
                            type="text"
                            value={improvementReplacementAddress}
                            onChange={(e) => !isReadOnly && setImprovementReplacementAddress(e.target.value)}
                            placeholder="Enter property address"
                            disabled={isReadOnly}
                            className="w-full"
                          />
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">What was the sale price?</h4>
                          <div className="relative">
                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/80 font-medium">$</span>
                            <Input
                              type="text"
                              value={improvementSalePrice}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9,.]/g, '')
                                !isReadOnly && setImprovementSalePrice(value)
                              }}
                              placeholder="0.00"
                              disabled={isReadOnly}
                              className="pl-8 w-full"
                            />
                          </div>
                        </div>

                        <div className="space-y-4">
                          <h4 className="text-lg font-semibold text-foreground">Is the replacement property currently improved, unimproved land, or partially improved?</h4>
                          <div className="space-y-3">
                            {['Improved', 'Unimproved land', 'Partially improved'].map((option) => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => !isReadOnly && setImprovementPropertyStatus(option)}
                                disabled={isReadOnly}
                                className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                                  improvementPropertyStatus === option
                                    ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                    : 'border-border bg-card hover:border-green-500'
                                } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                              >
                                <span className={`font-medium ${
                                  improvementPropertyStatus === option
                                    ? 'text-green-700 dark:text-green-300'
                                    : 'text-green-700 dark:text-green-400'
                                }`}>
                                  {option}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Improvement/Construction Exchange - Construction Scope */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Construction Scope</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Will exchange funds be used for:</h4>
                      <div className="space-y-3">
                        {['New construction', 'Renovation / improvements', 'Both'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setImprovementConstructionScope(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              improvementConstructionScope === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              improvementConstructionScope === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Estimated total construction/improvement budget</h4>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground/80 font-medium">$</span>
                        <Input
                          type="text"
                          value={improvementConstructionBudget}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9,.]/g, '')
                            !isReadOnly && setImprovementConstructionBudget(value)
                          }}
                          placeholder="0.00"
                          disabled={isReadOnly}
                          className="pl-8 w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Improvement/Construction Exchange - Timing & Control */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Timing & Control</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Do you understand that all improvements must be completed and value added before the 180-day exchange deadline?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setImprovement180DeadlineAware(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              improvement180DeadlineAware === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              improvement180DeadlineAware === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Will improvements be completed while the property is held by an exchange accommodation entity (EAT)?</h4>
                      <p className="text-sm text-muted-foreground">(Informational acknowledgment)</p>
                      <div className="space-y-3">
                        {['Yes', 'No', 'What is this?'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setImprovementEATAware(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              improvementEATAware === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              improvementEATAware === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                      {improvementEATAware === 'What is this?' && (
                        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                          <div className="text-sm font-semibold text-foreground">Why does an EAT matter here?</div>
                          <div className="mt-1 text-sm text-foreground/80">
                            For many improvement/construction exchanges, an EAT (or similar parking entity) may hold title while improvements are completed. That way, the value added by the work can be included in the replacement property value within the exchange timeframe.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Improvement/Construction Exchange - Contractors & Structure */}
                  <div className="space-y-6 border-t border-border pt-6">
                    <h3 className="text-xl font-bold text-foreground">Contractors & Structure</h3>
                    
                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Do you already have a contractor selected?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setImprovementContractorSelected(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              improvementContractorSelected === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              improvementContractorSelected === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h4 className="text-lg font-semibold text-foreground">Are construction contracts already signed?</h4>
                      <div className="space-y-3">
                        {['Yes', 'No'].map((option) => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => !isReadOnly && setImprovementContractsSigned(option)}
                            disabled={isReadOnly}
                            className={`w-full px-4 py-3 text-left rounded-lg border-2 transition-all ${
                              improvementContractsSigned === option
                                ? 'border-green-600 dark:border-green-400 bg-green-50 dark:bg-green-900/20'
                                : 'border-border bg-card hover:border-green-500'
                            } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <span className={`font-medium ${
                              improvementContractsSigned === option
                                ? 'text-green-700 dark:text-green-300'
                                : 'text-green-700 dark:text-green-400'
                            }`}>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {exchangeInfoReady && (
                <Card className="p-4 border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20 animate-pulse-slow">
                  <label htmlFor="confirm-exchange-info" className="flex items-center justify-between gap-4 cursor-pointer">
                    <span className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                      I confirm these answers are complete.
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="confirm-exchange-info"
                        checked={exchangeInfoConfirmed}
                        onChange={(e) => {
                          if (isReadOnly) return
                          const checked = e.target.checked
                          setExchangeInfoConfirmed(checked)
                          if (checked) {
                            activityLog.logSalesHubProgress('Exchange information confirmed')
                            onPhase1StepChange?.('upload-documents')
                          }
                        }}
                        disabled={isReadOnly}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 rounded-full bg-muted peer-checked:bg-emerald-600 transition-colors"></div>
                      <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </Card>
              )}
            </div>
          </div>
        )
      }
      
      if (phase1Step === 'upload-documents') {
        const uploadedCount = (salesPurchaseAgreement ? 1 : 0) + (preliminaryTitleReport ? 1 : 0)
        const hasRequiredDoc = !!salesPurchaseAgreement
        const hasTitleReportIfNeeded = !isInEscrow || !!preliminaryTitleReport
        const documentsReadyForConfirm = hasRequiredDoc && hasTitleReportIfNeeded && hasExtractedWithNeela && (!preliminaryTitleReport || hasTitleReportExtracted)
        
        return (
          <div className="h-full overflow-y-auto">
            {guidance && !isReadOnly && (
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{guidance.message}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">{guidance.actionNeeded}</p>
                  </div>
                </div>
              </div>
            )}
            {/* Documents Count Summary */}
            <Card className="p-4 mb-4 bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 border-2 border-primary-200 dark:border-primary-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-800 rounded-lg">
                    <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Documents Uploaded</h3>
                    <p className="text-xs text-muted-foreground">
                      Sales & Purchase Agreement{isInEscrow ? ', Preliminary Title Report' : ' (+ Title Report if in escrow)'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{uploadedCount}</p>
                  <p className="text-xs text-muted-foreground">Required docs uploaded</p>
                </div>
              </div>
            </Card>

            <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent px-4 py-3 mb-4">
              <div className="text-sm font-semibold text-foreground">Neela will extract details for you</div>
              <div className="mt-1 text-sm text-foreground/80 leading-relaxed">
                Upload your documents, then run extraction. Neela can pre‑fill common fields across Sales Hub (parties, addresses, dates, and amounts) so you don’t have to re‑type what’s already in your paperwork.
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Sales & Purchase Agreement */}
              <Card className="p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-primary-600" />
                    <h4 className="text-sm font-semibold text-foreground/80">Sales & Purchase Agreement</h4>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Neela extracts info</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-col">
                  {salesPurchaseAgreement ? (
                    <div className="space-y-3 flex-1 flex flex-col">
                      <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                            <p className="text-sm font-medium text-green-800 dark:text-green-200">Uploaded</p>
                          </div>
                          <p className="text-xs text-green-700 dark:text-green-300 ml-7 truncate">{salesPurchaseAgreement.name}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSalesPurchaseAgreement(null)
                            setHasExtractedWithNeela(false)
                            setSaleExtractionPhase('idle')
                            setSaleExtractedData(null)
                            setUploadConfirmed(false)
                          }}
                          disabled={isReadOnly}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="in-escrow"
                            checked={isInEscrow}
                          onChange={(e) => {
                            setIsInEscrow(e.target.checked)
                            setUploadConfirmed(false)
                          }}
                            disabled={isReadOnly}
                            className="h-3.5 w-3.5 rounded border-border text-primary focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <label htmlFor="in-escrow" className="text-xs font-medium text-foreground/80 cursor-pointer">
                            Property is in escrow
                          </label>
                        </div>
                        {isInEscrow && !preliminaryTitleReport && (
                          <div className="flex items-start space-x-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-800 dark:text-amber-200">
                              Preliminary Title Report is required for properties in escrow
                            </p>
                          </div>
                        )}
                      </div>
                      {saleExtractionPhase === 'uploading' && (
                        <div
                          className="rounded-xl border border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/20 p-4 flex items-center gap-3"
                          role="status"
                          aria-live="polite"
                        >
                          <span
                            className="h-5 w-5 rounded-full border-2 border-green-500 border-t-transparent animate-spin flex-shrink-0"
                            aria-hidden="true"
                          />
                          <p className="text-sm text-green-800 dark:text-green-200">
                            Uploading {salesPurchaseAgreement?.name ?? ''}…
                          </p>
                        </div>
                      )}
                      {saleExtractionPhase === 'extracting' && (
                        <SaleNeelaExtracting fileName={salesPurchaseAgreement?.name ?? ''} />
                      )}
                      {saleExtractionPhase === 'revealed' && saleExtractedData !== null && (
                        <>
                          <SaleNeelaReveal extracted={saleExtractedData} />
                          {!isReadOnly && (
                            <div className="flex justify-end mt-2">
                              <button
                                type="button"
                                aria-label="Replace Sales & Purchase Agreement"
                                onClick={() => {
                                  setSalesPurchaseAgreement(null)
                                  setHasExtractedWithNeela(false)
                                  setSaleExtractionPhase('idle')
                                  setSaleExtractedData(null)
                                  setUploadConfirmed(false)
                                }}
                                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                              >
                                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                                Replace document
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      {saleExtractionPhase === 'idle' && !hasExtractedWithNeela && (
                        <button
                          onClick={async () => {
                            if (isInEscrow && !preliminaryTitleReport) {
                              toast.error('Please upload the Preliminary Title Report before continuing (required for properties in escrow)')
                              return
                            }
                            if (!salesPurchaseAgreement || isReadOnly) return
                            setSaleExtractionPhase('extracting')
                            setIsExtracting(true)
                            try {
                              if (isDemoUser && mode === 'phase1' && demoData) {
                                const demoExchanger = demoData.exchanger
                                const nameParts = demoExchanger.names ? parseFullName(demoExchanger.names) : getUserNameParts()
                                const homeAddress = demoExchanger.homeAddress || ''
                                setExchangers([{
                                  ...nameParts,
                                  names: demoExchanger.names,
                                  email: user?.email || '',
                                  phone: user?.phone || '',
                                  homeAddress,
                                  street: demoExchanger.street || '',
                                  city: demoExchanger.city || '',
                                  state: demoExchanger.state || '',
                                  zipCode: demoExchanger.zipCode || '',
                                  taxIdNumber: demoExchanger.taxIdNumber || '',
                                  taxClassification: demoExchanger.taxClassification || 'individual',
                                  beneficialInterest: '100',
                                }])
                                setExchangerInfoPhaseOne(prev => ({
                                  ...prev,
                                  ...nameParts,
                                  names: demoExchanger.names,
                                  email: user?.email || prev.email || '',
                                  phone: user?.phone || prev.phone || '',
                                  homeAddress,
                                  street: demoExchanger.street || prev.street,
                                  city: demoExchanger.city || prev.city,
                                  state: demoExchanger.state || prev.state,
                                  zipCode: demoExchanger.zipCode || prev.zipCode,
                                  taxIdNumber: demoExchanger.taxIdNumber || prev.taxIdNumber,
                                }))
                                setBuyerInfo({ names: demoData.buyer.names })
                                setRelinquishedProperty(prev => ({
                                  ...prev,
                                  addressOrLegalDescription: demoData.property.addressOrLegalDescription || prev.addressOrLegalDescription,
                                  closingDate: demoData.property.closingDate || prev.closingDate,
                                }))
                                exchangeTypeRef.current = 'Forward exchange'
                                propertySaleStatusRef.current = 'Under contract'
                                mortgagesPaidOffRef.current = 'No'
                                setExchangeType('Forward exchange')
                                setPropertySaleStatus('Under contract')
                                setMortgagesPaidOff('No')
                                const userEnteredListPrice = (listPriceRef.current || '').replace(/[^0-9.]/g, '')
                                const hasUserListPrice = userEnteredListPrice !== '' && Number(userEnteredListPrice) > 0
                                if (!hasUserListPrice) {
                                  listPriceRef.current = '1000000'
                                  setListPrice('1000000')
                                }
                                const demoReveal: SaleDocumentExtracted = {
                                  exchangerName: demoExchanger.names,
                                  buyerName: demoData.buyer.names,
                                  propertyAddress: demoData.property.addressOrLegalDescription,
                                  closingDate: demoData.property.closingDate,
                                }
                                setSaleExtractedData(demoReveal)
                                setHasExtractedWithNeela(true)
                                setSaleExtractionPhase('revealed')
                                setExchangersReviewed(false)
                                setPropertyReviewed(false)
                                setTimeout(() => saveProgressToStorage(), 100)
                                window.dispatchEvent(new CustomEvent('extractedDatesAvailable', {
                                  detail: buildDemo1ExtractedDates(),
                                }))
                                toast.success('Neela AI extracted information from the Sales & Purchase Agreement!')
                                return
                              }
                              const extracted = await documentExtraction.simulateExtraction(salesPurchaseAgreement, 'purchase-agreement')
                              if (extracted.exchangerNames) {
                                const names = extracted.exchangerNames as string
                                const nameParts = parseFullName(names)
                                const homeAddress = extracted.exchangerAddress ? `${extracted.exchangerAddress.street || ''}, ${extracted.exchangerAddress.city || ''}, ${extracted.exchangerAddress.state || ''} ${extracted.exchangerAddress.zipCode || ''}`.trim() : ''
                                setExchangers(prev => prev.length > 0 ? prev.map((e, i) => i === 0 ? {
                                  ...e,
                                  ...nameParts,
                                  names,
                                  email: user?.email || e.email || '',
                                  phone: user?.phone || e.phone || '',
                                  homeAddress,
                                  street: extracted.exchangerAddress?.street || e.street || '',
                                  city: extracted.exchangerAddress?.city || e.city || '',
                                  state: extracted.exchangerAddress?.state || e.state || '',
                                  zipCode: extracted.exchangerAddress?.zipCode || e.zipCode || '',
                                  taxIdNumber: extracted.exchangerTaxId || e.taxIdNumber || '',
                                  beneficialInterest: e.beneficialInterest || '100',
                                } : e) : [{
                                  ...nameParts,
                                  names,
                                  email: user?.email || '',
                                  phone: user?.phone || '',
                                  homeAddress,
                                  street: extracted.exchangerAddress?.street || '',
                                  city: extracted.exchangerAddress?.city || '',
                                  state: extracted.exchangerAddress?.state || '',
                                  zipCode: extracted.exchangerAddress?.zipCode || '',
                                  taxIdNumber: extracted.exchangerTaxId || '',
                                  beneficialInterest: '100',
                                }])
                                setExchangerInfoPhaseOne(prev => ({
                                  ...prev,
                                  ...nameParts,
                                  names,
                                  homeAddress: extracted.exchangerAddress ? `${extracted.exchangerAddress.street || ''}, ${extracted.exchangerAddress.city || ''}, ${extracted.exchangerAddress.state || ''} ${extracted.exchangerAddress.zipCode || ''}`.trim() : prev.homeAddress,
                                  street: extracted.exchangerAddress?.street || prev.street,
                                  city: extracted.exchangerAddress?.city || prev.city,
                                  state: extracted.exchangerAddress?.state || prev.state,
                                  zipCode: extracted.exchangerAddress?.zipCode || prev.zipCode,
                                  taxIdNumber: extracted.exchangerTaxId || prev.taxIdNumber,
                                }))
                              }
                              if (extracted.buyerNames) {
                                setBuyerInfo({ names: extracted.buyerNames as string })
                              }
                              if (extracted.closingCompanyName) {
                                if (!isDemoUser || mode !== 'phase1') {
                                  setClosingCompanySale(prev => ({
                                    ...prev,
                                    name: extracted.closingCompanyName || prev.name,
                                    address: extracted.closingCompanyAddress || prev.address,
                                    phone: extracted.closingCompanyPhone || prev.phone,
                                    closerName: extracted.closerName || prev.closerName,
                                    closerEmail: extracted.closerEmail || prev.closerEmail,
                                  }))
                                }
                              }
                              let resolvedPropertyAddress = ''
                              if (extracted.propertyAddress) {
                                const addr = extracted.propertyAddress
                                resolvedPropertyAddress = `${addr.street || ''}, ${addr.city || ''}, ${addr.state || ''} ${addr.zipCode || ''}`.trim()
                                setRelinquishedProperty(prev => ({
                                  ...prev,
                                  addressOrLegalDescription: resolvedPropertyAddress,
                                  closingDate: (extracted.closingDate as string | undefined) || prev.closingDate,
                                }))
                                dataPersistence.merge({ propertyAddress: addr })
                              }
                              const revealData: SaleDocumentExtracted = {
                                exchangerName: extracted.exchangerNames as string | undefined,
                                buyerName: extracted.buyerNames as string | undefined,
                                propertyAddress: resolvedPropertyAddress || undefined,
                                closingCompanyName: extracted.closingCompanyName as string | undefined,
                                closingCompanyAddress: extracted.closingCompanyAddress as string | undefined,
                                closerName: extracted.closerName as string | undefined,
                                purchaseAgreementDate: extracted.purchaseAgreementDate as string | undefined,
                                closingDate: extracted.closingDate as string | undefined,
                                purchasePrice: extracted.purchasePrice as number | undefined,
                              }
                              setSaleExtractedData(revealData)
                              setHasExtractedWithNeela(true)
                              setSaleExtractionPhase('revealed')
                              setExchangersReviewed(false)
                              setPropertyReviewed(false)
                              toast.success('Neela AI extracted information from the Sales & Purchase Agreement!')
                            } catch {
                              toast.error('Failed to extract data from document')
                              setSaleExtractionPhase('idle')
                            } finally {
                              setIsExtracting(false)
                            }
                          }}
                          disabled={isExtracting || (isInEscrow && !preliminaryTitleReport) || isReadOnly}
                          aria-label="Extract Sales and Purchase Agreement with Neela AI"
                          className={`group relative w-full mt-auto px-4 py-3 font-semibold rounded-lg shadow-lg transition-all duration-200 overflow-hidden ${
                            isExtracting || (isInEscrow && !preliminaryTitleReport) || isReadOnly
                              ? 'bg-muted text-muted-foreground cursor-not-allowed'
                              : 'bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white hover:shadow-xl'
                          }`}
                        >
                          {!isExtracting && !(isInEscrow && !preliminaryTitleReport) && (
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                          )}
                          <span className="relative flex items-center justify-center text-sm">
                            {isExtracting ? (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                                Neela processing via AI...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                Extract with Neela AI
                              </>
                            )}
                          </span>
                        </button>
                      )}
                      {saleExtractionPhase === 'idle' && hasExtractedWithNeela && (
                        <div className="mt-auto">
                          <div className="flex items-center justify-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                            <p className="text-sm font-semibold text-green-800 dark:text-green-200">Extracted with Neela AI</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 flex-1 flex flex-col">
                      <label className="flex flex-col items-center justify-center w-full flex-1 min-h-[120px] border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted transition-colors">
                        <div className="flex flex-col items-center justify-center">
                          <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground text-center px-2">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX, images</p>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              setSalesPurchaseAgreement(file)
                              setHasExtractedWithNeela(false)
                              setSaleExtractionPhase('idle')
                              setSaleExtractedData(null)
                              setUploadConfirmed(false)
                              activityLog.logDocumentUploaded('Sales & Purchase Agreement')
                              setTimeout(() => saveProgressToStorage(), 100)
                            }
                          }}
                        />
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1"
                          size="sm"
                          disabled={isReadOnly}
                        >
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          Upload
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => cameraInputRef.current?.click()}
                          className="flex-1"
                          size="sm"
                          disabled={isReadOnly}
                        >
                          <Camera className="h-3.5 w-3.5 mr-1.5" />
                          Camera
                        </Button>
                      </div>
                      <input
                        ref={cameraInputRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        onChange={async (e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setSalesPurchaseAgreement(file)
                            setHasExtractedWithNeela(false)
                            setSaleExtractionPhase('idle')
                            setSaleExtractedData(null)
                            setUploadConfirmed(false)
                            activityLog.logDocumentUploaded('Sales & Purchase Agreement')
                            setTimeout(() => saveProgressToStorage(), 100)
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </Card>

              {/* Preliminary Title Report */}
              <Card className="p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-primary-600" />
                    <h4 className="text-sm font-semibold text-foreground/80">
                      Preliminary Title Report {isInEscrow ? (
                        <span className="text-xs text-red-600 dark:text-red-400 font-semibold">*</span>
                      ) : (
                        <span className="text-xs text-muted-foreground font-normal">(Optional)</span>
                      )}
                    </h4>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Neela extracts info</span>
                  </div>
                </div>
                <div className="flex-1 flex flex-col">
                  {preliminaryTitleReport ? (
                    <div className="space-y-3 flex-1 flex flex-col">
                      <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                            <p className="text-sm font-medium text-green-800 dark:text-green-200">Uploaded</p>
                          </div>
                          <p className="text-xs text-green-700 dark:text-green-300 ml-7 truncate">{preliminaryTitleReport.name}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (isReadOnly) return
                            setPreliminaryTitleReport(null)
                            setHasTitleReportExtracted(false)
                            setTitleExtractionPhase('idle')
                            setTitleExtractedData(null)
                            setUploadConfirmed(false)
                            if (isDemoUser && mode === 'phase1') {
                              setClosingCompanySale({
                                name: '',
                                address: '',
                                phone: '',
                                email: '',
                                closerName: '',
                                closerEmail: ''
                              })
                            }
                          }}
                          disabled={isReadOnly}
                          className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                      {titleExtractionPhase === 'extracting' && (
                        <SaleNeelaExtracting
                          fileName={preliminaryTitleReport?.name ?? ''}
                          documentLabel="Preliminary Title Report"
                        />
                      )}
                      {titleExtractionPhase === 'revealed' && titleExtractedData !== null && (
                        <>
                          <SaleNeelaReveal extracted={titleExtractedData} />
                          {!isReadOnly && (
                            <div className="flex justify-end mt-2">
                              <button
                                type="button"
                                aria-label="Replace Preliminary Title Report"
                                onClick={() => {
                                  setPreliminaryTitleReport(null)
                                  setHasTitleReportExtracted(false)
                                  setTitleExtractionPhase('idle')
                                  setTitleExtractedData(null)
                                  setUploadConfirmed(false)
                                  if (isDemoUser && mode === 'phase1') {
                                    setClosingCompanySale({
                                      name: '',
                                      address: '',
                                      phone: '',
                                      email: '',
                                      closerName: '',
                                      closerEmail: ''
                                    })
                                  }
                                }}
                                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                              >
                                <RefreshCw className="h-3 w-3" aria-hidden="true" />
                                Replace document
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      {titleExtractionPhase === 'idle' && !hasTitleReportExtracted && (
                        <button
                          onClick={async () => {
                            if (!preliminaryTitleReport || isReadOnly) return
                            setIsExtractingTitleReport(true)
                            setTitleExtractionPhase('extracting')
                            try {
                              const extracted = await documentExtraction.simulateExtraction(
                                preliminaryTitleReport,
                                'title-report'
                              )
                              const demoClosingCompany = isDemoUser && mode === 'phase1' ? demoData?.closingCompany : null
                              const resolvedName = demoClosingCompany?.name || (extracted.closingCompanyName as string | undefined) || ''
                              const resolvedAddress = demoClosingCompany?.address || (extracted.closingCompanyAddress as string | undefined) || ''
                              const resolvedPhone = demoClosingCompany?.phone || (extracted.closingCompanyPhone as string | undefined) || ''
                              const resolvedEmail = demoClosingCompany?.email || (extracted.closingCompanyEmail as string | undefined) || ''
                              const resolvedCloserName = demoClosingCompany?.closerName || (extracted.closerName as string | undefined) || ''
                              const resolvedCloserEmail = demoClosingCompany?.closerEmail || (extracted.closerEmail as string | undefined) || ''
                              setClosingCompanySale(prev => ({
                                ...prev,
                                name: resolvedName || prev.name,
                                address: resolvedAddress || prev.address,
                                phone: resolvedPhone || prev.phone,
                                email: resolvedEmail || prev.email,
                                closerName: resolvedCloserName || prev.closerName,
                                closerEmail: resolvedCloserEmail || prev.closerEmail,
                              }))
                              if (isDemoUser && mode === 'phase1' && demoData?.property?.closingDate) {
                                setRelinquishedProperty(prev => ({
                                  ...prev,
                                  closingDate: prev.closingDate || demoData.property.closingDate,
                                }))
                              }
                              dataPersistence.merge({
                                closingCompanySale: {
                                  name: resolvedName,
                                  address: resolvedAddress,
                                  phone: resolvedPhone,
                                  email: resolvedEmail,
                                  closerName: resolvedCloserName,
                                  closerEmail: resolvedCloserEmail,
                                },
                              })
                              setDocuments(prev => prev.map(doc =>
                                doc.name === 'Preliminary Title Report'
                                  ? { ...doc, uploaded: true, file: preliminaryTitleReport }
                                  : doc
                              ))
                              const revealData: SaleDocumentExtracted = {
                                closingCompanyName: resolvedName || undefined,
                                closingCompanyAddress: resolvedAddress || undefined,
                                closerName: resolvedCloserName || undefined,
                              }
                              setTitleExtractedData(revealData)
                              setHasTitleReportExtracted(true)
                              setTitleExtractionPhase('revealed')
                              setTimeout(() => saveProgressToStorage(), 100)
                              toast.success('Neela AI extracted title and closing company information!')
                            } catch {
                              toast.error('Failed to extract information. Please try again.')
                              setTitleExtractionPhase('idle')
                            } finally {
                              setIsExtractingTitleReport(false)
                            }
                          }}
                          disabled={isExtractingTitleReport || isReadOnly}
                          aria-label="Extract Preliminary Title Report with Neela AI"
                          className={`group relative w-full mt-auto px-4 py-3 font-semibold rounded-lg shadow-lg transition-all duration-200 overflow-hidden ${
                            isExtractingTitleReport || isReadOnly
                              ? 'bg-muted text-muted-foreground cursor-not-allowed'
                              : 'bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600 text-white hover:shadow-xl'
                          }`}
                        >
                          {!isExtractingTitleReport && !isReadOnly && (
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                          )}
                          <span className="relative flex items-center justify-center text-sm">
                            {isExtractingTitleReport ? (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 animate-spin" />
                                Neela processing via AI...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform duration-200" />
                                Extract with Neela AI
                              </>
                            )}
                          </span>
                        </button>
                      )}
                      {titleExtractionPhase === 'idle' && hasTitleReportExtracted && (
                        <div className="mt-auto">
                          <div className="flex items-center justify-center p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg">
                            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                            <p className="text-sm font-semibold text-green-800 dark:text-green-200">Extracted with Neela AI</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 flex-1 flex flex-col">
                      <label className="flex flex-col items-center justify-center w-full flex-1 min-h-[120px] border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted transition-colors">
                        <div className="flex flex-col items-center justify-center">
                          <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                          <p className="text-xs text-muted-foreground text-center px-2">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">PDF, DOC, DOCX, images</p>
                        </div>
                        <input
                          ref={titleReportInputRef}
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx,image/*"
                          disabled={isReadOnly}
                          onChange={(e) => {
                            if (isReadOnly) return
                            const file = e.target.files?.[0]
                            if (file) {
                              setPreliminaryTitleReport(file)
                              activityLog.logDocumentUploaded('Preliminary Title Report')
                              setTimeout(() => saveProgressToStorage(), 100)
                            }
                          }}
                        />
                      </label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => titleReportInputRef.current?.click()}
                          className="flex-1"
                          size="sm"
                          disabled={isReadOnly}
                        >
                          <Upload className="h-3.5 w-3.5 mr-1.5" />
                          Upload
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => titleReportCameraRef.current?.click()}
                          className="flex-1"
                          size="sm"
                          disabled={isReadOnly}
                        >
                          <Camera className="h-3.5 w-3.5 mr-1.5" />
                          Camera
                        </Button>
                      </div>
                      <input
                        ref={titleReportCameraRef}
                        type="file"
                        className="hidden"
                        accept="image/*"
                        capture="environment"
                        disabled={isReadOnly}
                        onChange={(e) => {
                          if (isReadOnly) return
                          const file = e.target.files?.[0]
                          if (file) {
                            setPreliminaryTitleReport(file)
                              setUploadConfirmed(false)
                              setUploadConfirmed(false)
                            activityLog.logDocumentUploaded('Preliminary Title Report')
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </Card>
            </div>
            {documentsReadyForConfirm && (
              <Card className="mt-4 p-4 border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20 animate-pulse-slow">
                <div className="flex items-center">
                  <label htmlFor="confirm-uploads" className="flex w-full items-center justify-between gap-4 cursor-pointer">
                    <span className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                      I confirm I'm ready to review data extracted with Neela AI.
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="confirm-uploads"
                        checked={uploadConfirmed}
                        onChange={(e) => {
                          if (isReadOnly) return
                          setUploadConfirmed(e.target.checked)
                          if (e.target.checked) {
                            activityLog.logSalesHubProgress('Upload documents confirmed')
                          }
                        }}
                        disabled={isReadOnly}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 rounded-full bg-muted peer-checked:bg-emerald-600 transition-colors"></div>
                      <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </div>
              </Card>
            )}
          </div>
        )
      }
      
      if (phase1Step === 'exchangers') {
        return (
          <div className="flex-1 overflow-y-scroll pr-2 min-h-0" style={{ maxHeight: '100%' }}>
            {guidance && !isReadOnly && (
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{guidance.message}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">{guidance.actionNeeded}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold">Exchanger Information</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setExchangers(prev => [...prev, { 
                  firstName: '',
                  middleName: '',
                  lastName: '',
                  names: '', 
                  email: '',
                  phone: '',
                  homeAddress: '', 
                  street: '', 
                  city: '', 
                  state: '', 
                  zipCode: '', 
                  taxIdNumber: '', 
                  beneficialInterest: '' 
                }])}
                disabled={exchangers.length >= 3 || isReadOnly}
                className={exchangers.length >= 3 || isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Exchanger {exchangers.length >= 3 ? '(Max 3)' : ''}
              </Button>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Important:</p>
                  <p>Only include exchangers who are listed on the deed of the relinquished property. If there are multiple exchangers, you can add up to 3.</p>
                </div>
              </div>
            </div>
            
            {exchangers.map((exchanger, index) => {
              // Calculate total beneficial interest for all exchangers
              const totalPercentage = exchangers.reduce((sum, ex) => {
                const percentage = ex.beneficialInterest || (exchangers.length === 1 ? '100' : '')
                const numValue = parseFloat(percentage) || 0
                return sum + numValue
              }, 0)
              
              // Calculate total excluding current exchanger to check if adding this value would exceed 100%
              const totalExcludingCurrent = exchangers.reduce((sum, ex, i) => {
                if (i === index) return sum
                const percentage = ex.beneficialInterest || (exchangers.length === 1 ? '100' : '')
                const numValue = parseFloat(percentage) || 0
                return sum + numValue
              }, 0)
              
              const currentValue = parseFloat(exchanger.beneficialInterest || (exchangers.length === 1 ? '100' : '')) || 0
              const wouldExceed100 = totalExcludingCurrent + currentValue > 100
              const isLessThan100 = exchangers.length > 1 && totalPercentage < 100 && totalPercentage > 0
              
              return (
              <Card key={index} className="p-4 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-foreground/80">Exchanger {index + 1}</h4>
                  {exchangers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setExchangers(prev => prev.filter((_, i) => i !== index))}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      disabled={isReadOnly}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">First Name *</label>
                    <Input
                      value={exchanger.firstName || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const firstName = e.target.value
                        const fullName = [firstName, updated[index].middleName, updated[index].lastName].filter(Boolean).join(' ')
                        updated[index] = {
                          ...updated[index],
                          firstName,
                          names: fullName
                        }
                        setExchangers(updated)
                      }}
                      placeholder="First name"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Middle Name</label>
                    <Input
                      value={exchanger.middleName || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const middleName = e.target.value
                        const fullName = [updated[index].firstName, middleName, updated[index].lastName].filter(Boolean).join(' ')
                        updated[index] = {
                          ...updated[index],
                          middleName,
                          names: fullName
                        }
                        setExchangers(updated)
                      }}
                      placeholder="Middle name (optional)"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Last Name *</label>
                    <Input
                      value={exchanger.lastName || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const lastName = e.target.value
                        const fullName = [updated[index].firstName, updated[index].middleName, lastName].filter(Boolean).join(' ')
                        updated[index] = {
                          ...updated[index],
                          lastName,
                          names: fullName
                        }
                        setExchangers(updated)
                      }}
                      placeholder="Last name"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Email *</label>
                    <Input
                      type="email"
                      value={exchanger.email || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        updated[index] = { ...updated[index], email: e.target.value }
                        setExchangers(updated)
                      }}
                      placeholder="exchanger@example.com"
                      readOnly={isReadOnly || index === 0}
                      disabled={isReadOnly || index === 0}
                      className={index === 0
                        ? 'bg-muted cursor-not-allowed border-green-300 dark:border-green-600 focus-visible:ring-green-400'
                        : 'border-green-300 dark:border-green-600 focus-visible:ring-green-400'}
                    />
                    {index === 0 && (
                      <p className="text-xs text-muted-foreground mt-1">Email comes from your profile</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Phone Number *</label>
                    <Input
                      type="tel"
                      value={exchanger.phone || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        updated[index] = { ...updated[index], phone: e.target.value }
                        setExchangers(updated)
                      }}
                      placeholder="(555) 123-4567"
                      readOnly={isReadOnly}
                      disabled={isReadOnly}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  {exchangers.length > 1 && index > 0 && (
                    <div className="md:col-span-2">
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-blue-800 dark:text-blue-200">
                            <strong>Note:</strong> This exchanger will receive an invitation to set up an account so they can view the exchange and e-sign their documents.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/80mb-1">Street Address (for W9) *</label>
                    <Input
                      value={exchanger.street || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const street = e.target.value
                        updated[index] = {
                          ...updated[index],
                          street,
                          homeAddress: `${street}, ${updated[index].city || ''}, ${updated[index].state || ''} ${updated[index].zipCode || ''}`.trim()
                        }
                        setExchangers(updated)
                      }}
                      placeholder="123 Main Street"
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">City *</label>
                    <Input
                      value={exchanger.city || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const city = e.target.value
                        updated[index] = {
                          ...updated[index],
                          city,
                          homeAddress: `${updated[index].street || ''}, ${city}, ${updated[index].state || ''} ${updated[index].zipCode || ''}`.trim()
                        }
                        setExchangers(updated)
                      }}
                      placeholder="Los Angeles"
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">State *</label>
                    <Input
                      value={exchanger.state || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const state = e.target.value.toUpperCase()
                        updated[index] = {
                          ...updated[index],
                          state,
                          homeAddress: `${updated[index].street || ''}, ${updated[index].city || ''}, ${state} ${updated[index].zipCode || ''}`.trim()
                        }
                        setExchangers(updated)
                      }}
                      placeholder="CA"
                      maxLength={2}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">ZIP Code *</label>
                    <Input
                      value={exchanger.zipCode || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        const zipCode = e.target.value
                        updated[index] = {
                          ...updated[index],
                          zipCode,
                          homeAddress: `${updated[index].street || ''}, ${updated[index].city || ''}, ${updated[index].state || ''} ${zipCode}`.trim()
                        }
                        setExchangers(updated)
                      }}
                      placeholder="90001"
                      maxLength={10}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/80mb-1">Exchanger's Tax ID Number *</label>
                    <div className="relative">
                      <Input
                        type={showTaxId[index] ? "text" : "password"}
                        value={exchanger.taxIdNumber}
                        onChange={(e) => {
                          const updated = [...exchangers]
                          updated[index] = { ...updated[index], taxIdNumber: e.target.value }
                          setExchangers(updated)
                        }}
                        placeholder="SSN or EIN"
                        className="pr-10 border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                      />
                      <button
                        type="button"
                        onClick={() => setShowTaxId(prev => ({ ...prev, [index]: !prev[index] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        title={showTaxId[index] ? "Hide" : "Show"}
                      >
                        {showTaxId[index] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/80mb-1">
                      Beneficial Interest (Ownership %) *
                    </label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={exchanger.beneficialInterest || (exchangers.length === 1 ? '100' : '')}
                        onChange={(e) => {
                          const newValue = e.target.value
                          const updated = [...exchangers]
                          updated[index] = { ...updated[index], beneficialInterest: newValue }
                          setExchangers(updated)
                        }}
                        placeholder={exchangers.length === 1 ? '100' : 'e.g., 50'}
                        className={cn(
                          'pr-12 border-green-300 dark:border-green-600 focus-visible:ring-green-400',
                          wouldExceed100 && 'border-red-500 dark:border-red-500'
                        )}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">%</span>
                    </div>
                    {wouldExceed100 && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-1 flex items-center space-x-1">
                        <AlertCircle className="h-3 w-3" />
                        <span>Total cannot exceed 100%. Current total would be {(totalExcludingCurrent + currentValue).toFixed(2)}%</span>
                      </p>
                    )}
                    {!wouldExceed100 && exchangers.length > 1 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Total: {totalPercentage.toFixed(2)}%
                      </p>
                    )}
                    {isLessThan100 && !wouldExceed100 && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mt-2">
                        <div className="flex items-start space-x-2">
                          <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                          <div className="text-xs text-yellow-800 dark:text-yellow-200">
                            <p className="font-medium mb-1">Total ownership is {totalPercentage.toFixed(2)}%, not 100%.</p>
                            <p>Is there an exchanger on the deed who is not participating in this 1031 exchange?</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {!wouldExceed100 && !isLessThan100 && exchangers.length === 1 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Percentage of ownership interest in the 1031 exchange
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/80 mb-1">
                      Federal Tax Classification (W-9 Box 3) *
                    </label>
                    <select
                      value={exchanger.taxClassification || ''}
                      onChange={(e) => {
                        const updated = [...exchangers]
                        updated[index] = { ...updated[index], taxClassification: e.target.value, llcTaxClassification: '' }
                        setExchangers(updated)
                      }}
                      disabled={isReadOnly}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select classification…</option>
                      <option value="individual">Individual / sole proprietor or single-member LLC</option>
                      <option value="c-corp">C Corporation</option>
                      <option value="s-corp">S Corporation</option>
                      <option value="partnership">Partnership</option>
                      <option value="trust-estate">Trust / estate</option>
                      <option value="llc">Limited liability company (LLC)</option>
                      <option value="other">Other</option>
                    </select>
                    {exchanger.taxClassification === 'llc' && (
                      <select
                        value={exchanger.llcTaxClassification || ''}
                        onChange={(e) => {
                          const updated = [...exchangers]
                          updated[index] = { ...updated[index], llcTaxClassification: e.target.value }
                          setExchangers(updated)
                        }}
                        disabled={isReadOnly}
                        className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        <option value="">LLC tax classification…</option>
                        <option value="C">C — taxed as C corporation</option>
                        <option value="S">S — taxed as S corporation</option>
                        <option value="P">P — taxed as partnership</option>
                      </select>
                    )}
                  </div>
                </div>
              </Card>
            )
            })}
            {isDemoUser && hasExtractedWithNeela && (() => {
              const missingFields: string[] = []
              exchangers.forEach((e, idx) => {
                const label = exchangers.length > 1 ? ` (Exchanger ${idx + 1})` : ''
                if (!e.firstName || !e.lastName) missingFields.push(`Full name${label}`)
                if (!e.email) missingFields.push(`Email${label}`)
                if (!e.phone) missingFields.push(`Phone${label}`)
                if (!(e.homeAddress || (e.street && e.city && e.state && e.zipCode))) missingFields.push(`Address${label}`)
                if (!e.taxIdNumber) missingFields.push(`Tax ID${label}`)
                if (!e.taxClassification) missingFields.push(`Tax Classification${label}`)
                if (e.taxClassification === 'llc' && !e.llcTaxClassification) missingFields.push(`LLC sub-type${label}`)
              })
              const fieldsReady = missingFields.length === 0
              return (
                <Card className="p-4 border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20 animate-pulse-slow">
                  {!fieldsReady && (
                    <div className="flex items-start gap-2 mb-3 text-sm text-amber-800 dark:text-amber-200">
                      <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
                      <span>Still needed: {missingFields.join(', ')}</span>
                    </div>
                  )}
                  <label
                    htmlFor="confirm-exchangers"
                    className={fieldsReady ? 'flex items-center justify-between gap-4 cursor-pointer' : 'flex items-center justify-between gap-4 cursor-not-allowed opacity-50'}
                  >
                    <span className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                      I confirm the exchanger information is accurate.
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="confirm-exchangers"
                        checked={exchangersReviewed}
                        onChange={(e) => { if (fieldsReady) setExchangersReviewed(e.target.checked) }}
                        disabled={isReadOnly || !fieldsReady}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 rounded-full bg-muted peer-checked:bg-emerald-600 transition-colors"></div>
                      <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </Card>
              )
            })()}
            </div>
          </div>
        )
      }
      
      if (phase1Step === 'property-details') {
        const buyerInfoLocked = isDemoUser && mode === 'phase1' && !hasExtractedWithNeela
        return (
          <div className="flex-1 overflow-y-scroll pr-2 min-h-0" style={{ maxHeight: '100%' }}>
            {guidance && !isReadOnly && (
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{guidance.message}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">{guidance.actionNeeded}</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-6">
              <div className="flex items-center space-x-2 mb-4">
                <MapPin className="h-5 w-5 text-primary-600" />
                <h3 className="text-lg font-semibold">Property & Buyer Details</h3>
              </div>
              
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground/80 mb-4">Relinquished Property</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/80mb-1">Relinquished Property Address or Legal Description *</label>
                    <Input
                      value={relinquishedProperty.addressOrLegalDescription}
                      onChange={(e) => setRelinquishedProperty(prev => ({ ...prev, addressOrLegalDescription: e.target.value }))}
                      placeholder="123 Property St, City, State ZIP or Legal description"
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closing Date *</label>
                    <Input
                      type="date"
                      value={relinquishedProperty.closingDate || ''}
                      onChange={(e) => {
                        const newDate = e.target.value
                        setRelinquishedProperty(prev => ({ ...prev, closingDate: newDate }))
                        if (newDate) {
                          localStorage.setItem('property_sale_date', new Date(newDate).toISOString())
                          window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
                        }
                      }}
                      className="dark:[color-scheme:dark] border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Date the property sale closed</p>
                  </div>
                </div>
              </Card>
              
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground/80 mb-4">Buyer Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground/80mb-1">Buyer's Names (for notice) *</label>
                    <Input
                      value={buyerInfoLocked ? '' : buyerInfo.names}
                      onChange={(e) => setBuyerInfo(prev => ({ ...prev, names: e.target.value }))}
                      placeholder="Full legal name(s) of buyer(s)"
                      disabled={isReadOnly || buyerInfoLocked}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <h4 className="text-sm font-medium text-foreground/80 mb-4">Title Company Information</h4>
                {isDemoUser && mode === 'phase1' && !hasTitleReportExtracted && (
                  <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
                    <div className="text-xs font-semibold text-amber-900 dark:text-amber-200">Upload & extract your Preliminary Title Report first</div>
                    <div className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                      Neela will pull the closing company and closer details from the Title Report. These fields stay blank until extraction runs.
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Name *</label>
                    <Input
                      value={closingCompanySale.name}
                      onChange={(e) => setClosingCompanySale(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="ABC Title Company"
                      disabled={isReadOnly || (isDemoUser && mode === 'phase1' && !hasTitleReportExtracted)}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Address *</label>
                    <Input
                      value={closingCompanySale.address}
                      onChange={(e) => setClosingCompanySale(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="123 Title St, City, State ZIP"
                      disabled={isReadOnly || (isDemoUser && mode === 'phase1' && !hasTitleReportExtracted)}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Phone *</label>
                    <Input
                      type="tel"
                      value={closingCompanySale.phone}
                      onChange={(e) => setClosingCompanySale(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      disabled={isReadOnly || (isDemoUser && mode === 'phase1' && !hasTitleReportExtracted)}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Email *</label>
                    <Input
                      type="email"
                      value={closingCompanySale.email || ''}
                      onChange={(e) => setClosingCompanySale(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="info@titleco.com"
                      disabled={isReadOnly || (isDemoUser && mode === 'phase1' && !hasTitleReportExtracted)}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closer (Closing Employee's Name) *</label>
                    <Input
                      value={closingCompanySale.closerName}
                      onChange={(e) => setClosingCompanySale(prev => ({ ...prev, closerName: e.target.value }))}
                      placeholder="John Smith"
                      disabled={isReadOnly || (isDemoUser && mode === 'phase1' && !hasTitleReportExtracted)}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground/80mb-1">Closer's Email *</label>
                    <Input
                      type="email"
                      value={closingCompanySale.closerEmail}
                      onChange={(e) => setClosingCompanySale(prev => ({ ...prev, closerEmail: e.target.value }))}
                      placeholder="closer@titleco.com"
                      disabled={isReadOnly || (isDemoUser && mode === 'phase1' && !hasTitleReportExtracted)}
                      className="border-green-300 dark:border-green-600 focus-visible:ring-green-400"
                    />
                  </div>
                </div>
              </Card>
              {isDemoUser && hasExtractedWithNeela && (
                <Card className="p-4 border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-900/20 animate-pulse-slow">
                  <label htmlFor="confirm-property-buyer" className="flex items-center justify-between gap-4 cursor-pointer">
                    <span className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                      I confirm the property and buyer details are accurate.
                    </span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="confirm-property-buyer"
                        checked={propertyReviewed}
                        onChange={(e) => setPropertyReviewed(e.target.checked)}
                        disabled={isReadOnly}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 rounded-full bg-muted peer-checked:bg-emerald-600 transition-colors"></div>
                      <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-background shadow-sm transition-transform peer-checked:translate-x-5"></div>
                    </div>
                  </label>
                </Card>
              )}
            </div>
          </div>
        )
      }
      
      if (phase1Step === 'signatures') {
        const step1Complete = isStepComplete(1)
        const step2Complete = isStepComplete(2)
        const docExtractionComplete = hasExtractedWithNeela && (!preliminaryTitleReport || hasTitleReportExtracted)
        const canSign = step1Complete && step2Complete && docExtractionComplete

        const blockedReason = !canSign
          ? [
              !hasExtractedWithNeela && 'Run Neela extraction on the Sales & Purchase Agreement',
              preliminaryTitleReport && !hasTitleReportExtracted && 'Run Neela extraction on the Preliminary Title Report',
              !step1Complete && 'Complete Exchanger Information',
              !step2Complete && 'Complete Property & Buyer Details',
            ]
              .filter(Boolean)
              .join('; ')
          : undefined

        const uploadedDocItems: EsignDocumentItem[] = [
          ...(salesPurchaseAgreement
            ? [
                {
                  key: 'spa-uploaded',
                  title: 'Sales & Purchase Agreement',
                  description: hasExtractedWithNeela
                    ? 'Uploaded and processed by Neela AI'
                    : `Uploaded: ${salesPurchaseAgreement.name}`,
                  signed: true,
                  actionType: 'view-only' as const,
                  onView: () => toast.success('Sales & Purchase Agreement is available for download.'),
                  disabled: false,
                },
              ]
            : []),
          ...(preliminaryTitleReport
            ? [
                {
                  key: 'title-report-uploaded',
                  title: 'Preliminary Title Report',
                  description: hasTitleReportExtracted
                    ? 'Uploaded and processed by Neela AI'
                    : `Uploaded: ${preliminaryTitleReport.name}`,
                  signed: true,
                  actionType: 'view-only' as const,
                  onView: () => toast.success('Preliminary Title Report is available for download.'),
                  disabled: false,
                },
              ]
            : []),
        ]

        const esignParties: EsignParty[] = [
          ...(uploadedDocItems.length > 0
            ? [{ label: 'Uploaded Documents', documents: uploadedDocItems, }]
            : []),
          ...exchangers.map((exchanger, index) => {
            const exchangerName = getExchangerFullName(exchanger) || (index === 0 ? getExchangerFullName(exchangerInfoPhaseOne) : '')
            const exchangerEmail = exchanger.email || (index === 0 ? exchangerInfoPhaseOne.email : '')
            const exchangeAgreementDoc = documents.find(d => d.name === `Exchange Agreement${index > 0 ? ` (${exchangerName || `Exchanger ${index + 1}`})` : ''}`)
            const w9Doc = documents.find(d => d.name === `W-9 Tax ID Certification${index > 0 ? ` (${exchangerName || `Exchanger ${index + 1}`})` : ''}`)
            const exchangeAgreement = index === 0
              ? (exchangeAgreementDoc || documents.find(d => d.name === 'Exchange Agreement'))
              : exchangeAgreementDoc
            const w9 = index === 0
              ? (w9Doc || documents.find(d => d.name === 'W-9 Tax ID Certification'))
              : w9Doc
            const partyLabel = `Exchanger ${index + 1}${exchangerName ? ` — ${exchangerName}` : ''}`
            const partyDocs: EsignDocumentItem[] = [
              {
                key: `exchange-agreement-${index}`,
                title: 'Exchange Agreement',
                description: 'Please review and e-sign the exchange agreement',
                signed: !!exchangeAgreement?.signed,
                actionType: 'e-sign',
                onSign: () => {
                  setSigningDoc(exchangeAgreement?.name || 'Exchange Agreement')
                  setSigningDocType('exchange-agreement')
                },
                onView: () => {
                  if (exchangeAgreement) {
                    setViewingDoc(exchangeAgreement.name)
                    setViewingDocType('exchange-agreement')
                  }
                },
                disabled: !canSign,
              },
              {
                key: `w9-${index}`,
                title: 'W-9 Tax ID Certification',
                description: 'Please review and e-sign the W-9 form',
                signed: !!w9?.signed,
                actionType: 'e-sign',
                onSign: () => {
                  setSigningDoc(w9?.name || 'W-9 Tax ID Certification')
                  setSigningDocType('w9')
                },
                onView: () => {
                  if (w9) {
                    setViewingDoc(w9.name)
                    setViewingDocType('w9')
                  }
                },
                disabled: !canSign,
              },
            ]
            return {
              label: partyLabel,
              email: exchangerEmail || undefined,
              documents: partyDocs,
            }
          }),
        ]

        return (
          <div className="flex-1 overflow-y-scroll pr-2 min-h-0" style={{ maxHeight: '100%' }}>
            {guidance && !isReadOnly && (
              <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 dark:border-blue-400 rounded-r-lg">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">{guidance.message}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">{guidance.actionNeeded}</p>
                  </div>
                </div>
              </div>
            )}
            <EsignDocumentsPanel
              parties={esignParties}
              canSign={canSign}
              blockedReason={blockedReason}
              readOnly={isReadOnly}
              color="green"
            />
          </div>
        )
      }
    }
    
    // Original mode handling
    switch(renderStep) {
      case 1: // Exchanger Information (Phase One)
        return (
          <div className="h-full overflow-y-auto">
            <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Exchanger Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80mb-1">First Name *</label>
                <Input
                  value={exchangerInfoPhaseOne.firstName || ''}
                  onChange={(e) => {
                    const firstName = e.target.value
                    const fullName = [firstName, exchangerInfoPhaseOne.middleName, exchangerInfoPhaseOne.lastName].filter(Boolean).join(' ')
                    setExchangerInfoPhaseOne(prev => ({ ...prev, firstName, names: fullName }))
                  }}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80mb-1">Middle Name</label>
                <Input
                  value={exchangerInfoPhaseOne.middleName || ''}
                  onChange={(e) => {
                    const middleName = e.target.value
                    const fullName = [exchangerInfoPhaseOne.firstName, middleName, exchangerInfoPhaseOne.lastName].filter(Boolean).join(' ')
                    setExchangerInfoPhaseOne(prev => ({ ...prev, middleName, names: fullName }))
                  }}
                  placeholder="Middle name (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80mb-1">Last Name *</label>
                <Input
                  value={exchangerInfoPhaseOne.lastName || ''}
                  onChange={(e) => {
                    const lastName = e.target.value
                    const fullName = [exchangerInfoPhaseOne.firstName, exchangerInfoPhaseOne.middleName, lastName].filter(Boolean).join(' ')
                    setExchangerInfoPhaseOne(prev => ({ ...prev, lastName, names: fullName }))
                  }}
                  placeholder="Last name"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Exchanger's Home Address (for W9) *</label>
                <Input
                  value={exchangerInfoPhaseOne.homeAddress}
                  onChange={(e) => setExchangerInfoPhaseOne(prev => ({ ...prev, homeAddress: e.target.value }))}
                  placeholder="123 Main St, City, State ZIP"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Exchanger's Tax ID Number *</label>
                <div className="relative">
                  <Input
                    type={showTaxIdPhaseOne ? "text" : "password"}
                    value={exchangerInfoPhaseOne.taxIdNumber}
                    onChange={(e) => setExchangerInfoPhaseOne(prev => ({ ...prev, taxIdNumber: e.target.value }))}
                    placeholder="SSN or EIN"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowTaxIdPhaseOne(!showTaxIdPhaseOne)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    title={showTaxIdPhaseOne ? "Hide" : "Show"}
                  >
                    {showTaxIdPhaseOne ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80 mb-1">
                  Federal Tax Classification (W-9 Box 3) *
                </label>
                <select
                  value={exchangerInfoPhaseOne.taxClassification || ''}
                  onChange={(e) => setExchangerInfoPhaseOne(prev => ({
                    ...prev,
                    taxClassification: e.target.value,
                    llcTaxClassification: '',
                  }))}
                  disabled={isReadOnly}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select classification…</option>
                  <option value="individual">Individual / sole proprietor or single-member LLC</option>
                  <option value="c-corp">C Corporation</option>
                  <option value="s-corp">S Corporation</option>
                  <option value="partnership">Partnership</option>
                  <option value="trust-estate">Trust / estate</option>
                  <option value="llc">Limited liability company (LLC)</option>
                  <option value="other">Other</option>
                </select>
                {exchangerInfoPhaseOne.taxClassification === 'llc' && (
                  <select
                    value={exchangerInfoPhaseOne.llcTaxClassification || ''}
                    onChange={(e) => setExchangerInfoPhaseOne(prev => ({
                      ...prev,
                      llcTaxClassification: e.target.value,
                    }))}
                    disabled={isReadOnly}
                    className="mt-2 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">LLC tax classification…</option>
                    <option value="C">C — taxed as C corporation</option>
                    <option value="S">S — taxed as S corporation</option>
                    <option value="P">P — taxed as partnership</option>
                  </select>
                )}
              </div>
            </div>
            </div>
          </div>
        )

      case 2: // Buyer Information
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Buyer Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Buyer's Names (for notice) *</label>
                <Input
                  value={buyerInfo.names}
                  onChange={(e) => setBuyerInfo(prev => ({ ...prev, names: e.target.value }))}
                  placeholder="Full legal name(s) of buyer(s)"
                />
              </div>
            </div>
          </div>
        )

      case 3: // Closing Company (Sale)
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <Building2 className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Closing Company (Sale)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Name *</label>
                <Input
                  value={closingCompanySale.name}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="ABC Title Company"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Address *</label>
                <Input
                  value={closingCompanySale.address}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="123 Title St, City, State ZIP"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Phone *</label>
                <Input
                  type="tel"
                  value={closingCompanySale.phone}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Closing Company Email *</label>
                <Input
                  type="email"
                  value={closingCompanySale.email || ''}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="info@titleco.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Closer (Closing Employee's Name) *</label>
                <Input
                  value={closingCompanySale.closerName}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, closerName: e.target.value }))}
                  placeholder="John Smith"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Closer's Email *</label>
                <Input
                  type="email"
                  value={closingCompanySale.closerEmail}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, closerEmail: e.target.value }))}
                  placeholder="closer@titleco.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">
                  Escrow / Title File Number{' '}
                  <span className="text-xs text-muted-foreground">(optional — shown on closing instructions)</span>
                </label>
                <Input
                  value={closingCompanySale.escrowFileNumber ?? ''}
                  onChange={(e) => setClosingCompanySale(prev => ({ ...prev, escrowFileNumber: e.target.value }))}
                  placeholder="PT-2026-001"
                />
              </div>
            </div>
          </div>
        )

      case 4: // Relinquished Property
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <MapPin className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Relinquished Property</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80mb-1">Relinquished Property Address or Legal Description *</label>
                <Input
                  value={relinquishedProperty.addressOrLegalDescription}
                  onChange={(e) => setRelinquishedProperty(prev => ({ ...prev, addressOrLegalDescription: e.target.value }))}
                  placeholder="123 Property St, City, State ZIP or Legal description"
                />
              </div>
            </div>
          </div>
        )

      case 6: { // Identification Form

        return (
          <div className="flex-1 overflow-y-scroll pr-2 min-h-0" style={{ maxHeight: '100%' }}>
            <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800 dark:text-red-200">
                  <p className="font-medium">Critical Deadline: 45 Days</p>
                  <p className="mt-1">
                    You must identify your replacement property(ies) within <strong>45 days</strong> of closing on the relinquished property.
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80 mb-1">
                  Exchanger's Names{' '}
                  {exchangerNamePrefilled ? (
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                      — confirmed in Sales Hub
                    </span>
                  ) : (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <Input
                  value={identificationForm.exchangerNames}
                  onChange={(e) => setIdentificationForm(prev => ({ ...prev, exchangerNames: e.target.value }))}
                  placeholder="Full legal name(s)"
                  disabled={isReadOnly || exchangerNamePrefilled}
                  className={exchangerNamePrefilled ? 'bg-slate-50 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400' : ''}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground/80 mb-1">
                  Relinquished Property Address or Legal Description{' '}
                  {relinquishedAddressPrefilled ? (
                    <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                      — confirmed in Sales Hub
                    </span>
                  ) : (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <Input
                  value={identificationForm.relinquishedPropertyAddress}
                  onChange={(e) => setIdentificationForm(prev => ({ ...prev, relinquishedPropertyAddress: e.target.value }))}
                  placeholder="123 Property St, City, State ZIP or Legal description"
                  disabled={isReadOnly || relinquishedAddressPrefilled}
                  className={relinquishedAddressPrefilled ? 'bg-slate-50 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400' : ''}
                />
              </div>

            </div>
            </div>
          </div>
        )
      }

      case 7: // Exchanger Information (Phase Two)
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Exchanger Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Exchanger's Names *</label>
                <Input
                  value={exchangerInfoPhaseTwo.names}
                  onChange={(e) => setExchangerInfoPhaseTwo(prev => ({ ...prev, names: e.target.value }))}
                  placeholder="Full legal name(s)"
                />
              </div>
            </div>
          </div>
        )

      case 8: // Seller Information
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Seller Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Seller's Names (for notice) *</label>
                <Input
                  value={sellerInfo.names}
                  onChange={(e) => setSellerInfo(prev => ({ ...prev, names: e.target.value }))}
                  placeholder="Full legal name(s) of seller(s)"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Authorized Signer on the Seller Acknowledgment{' '}
                  <span className="text-xs text-muted-foreground">(optional — shown on the Notice to Seller)</span>
                </label>
                <Input
                  value={sellerInfo.acknowledgmentSigner ?? ''}
                  onChange={(e) => setSellerInfo(prev => ({ ...prev, acknowledgmentSigner: e.target.value }))}
                  placeholder="Jane Q. Public, Managing Member"
                />
              </div>
            </div>
          </div>
        )

      case 9: // Closing Company (Purchase)
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <Building2 className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Closing Company (Purchase)</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Closing Company Name *</label>
                <Input
                  value={closingCompanyPurchase.name}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="XYZ Title Company"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Closing Company Address *</label>
                <Input
                  value={closingCompanyPurchase.address}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="456 Title Ave, City, State ZIP"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Closing Company Phone *</label>
                <Input
                  type="tel"
                  value={closingCompanyPurchase.phone}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Closing Company Email *</label>
                <Input
                  type="email"
                  value={closingCompanyPurchase.email || ''}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="info@titleco.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Closer (Closing Employee's Name) *</label>
                <Input
                  value={closingCompanyPurchase.closerName}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, closerName: e.target.value }))}
                  placeholder="Jane Doe"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Closer's Email *</label>
                <Input
                  type="email"
                  value={closingCompanyPurchase.closerEmail}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, closerEmail: e.target.value }))}
                  placeholder="closer@newtitleco.com"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Escrow / Title File Number{' '}
                  <span className="text-xs text-muted-foreground">(optional — shown on Notice to Seller)</span>
                </label>
                <Input
                  value={closingCompanyPurchase.escrowFileNumber ?? ''}
                  onChange={(e) => setClosingCompanyPurchase(prev => ({ ...prev, escrowFileNumber: e.target.value }))}
                  placeholder="AHT-2026-007"
                />
              </div>
            </div>
          </div>
        )

      case 10: // Replacement Property
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <MapPin className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Replacement Property</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Replacement Property Address or Legal Description *</label>
                <Input
                  value={replacementProperty.addressOrLegalDescription}
                  onChange={(e) => setReplacementProperty(prev => ({ ...prev, addressOrLegalDescription: e.target.value }))}
                  placeholder="456 New Property Ave, City, State ZIP or Legal description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Purchase Agreement Date{' '}
                  <span className="text-xs text-muted-foreground">(used in the Replacement Property Assignment)</span>
                </label>
                <Input
                  type="date"
                  value={replacementProperty.purchaseAgreementDate ?? ''}
                  onChange={(e) => setReplacementProperty(prev => ({ ...prev, purchaseAgreementDate: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-1">
                  Anticipated Close Date{' '}
                  <span className="text-xs text-muted-foreground">(shown on the closing instructions)</span>
                </label>
                <Input
                  type="date"
                  value={replacementProperty.closeDate ?? ''}
                  onChange={(e) => setReplacementProperty(prev => ({ ...prev, closeDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
        )

      case 11: // Disbursement Form
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <DollarSign className="h-5 w-5 text-primary-600" />
              <h3 className="text-lg font-semibold">Disbursement Form</h3>
            </div>
            {escrowBalance > 0 && (
              <div className="rounded-lg border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 p-4 flex items-center justify-between">
                <span className="text-sm font-medium text-orange-800 dark:text-orange-200">Available Escrow Balance</span>
                <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(escrowBalance)}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Exchanger's Names *</label>
                <Input
                  value={disbursementForm.exchangerNames}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, exchangerNames: e.target.value }))}
                  placeholder="Full legal name(s)"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Amount of Disbursement *</label>
                <Input
                  type="text"
                  value={disbursementForm.amount}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder={escrowBalance > 0
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(escrowBalance)
                    : '$500,000'}
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
                {escrowBalance > 0 && disbursementForm.amount && (() => {
                  const numeric = parseFloat(disbursementForm.amount.replace(/[^0-9.]/g, ''))
                  return !isNaN(numeric) && numeric > escrowBalance ? (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Amount exceeds available escrow balance of {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(escrowBalance)}
                    </p>
                  ) : null
                })()}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Wire Recipient *</label>
                <Input
                  value={disbursementForm.wireRecipient}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, wireRecipient: e.target.value }))}
                  placeholder="Recipient name"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Wire Recipient Address *</label>
                <Input
                  value={disbursementForm.wireRecipientAddress}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, wireRecipientAddress: e.target.value }))}
                  placeholder="123 Main St, City, State ZIP"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Beneficiary Name *</label>
                <Input
                  value={disbursementForm.beneficiaryName}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, beneficiaryName: e.target.value }))}
                  placeholder="Name on the receiving bank account"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Wire Recipient Bank Name *</label>
                <Input
                  value={disbursementForm.wireRecipientBankName}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, wireRecipientBankName: e.target.value }))}
                  placeholder="Bank Name"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Wire Recipient Bank ABA Number *</label>
                <Input
                  value={disbursementForm.wireRecipientBankABA}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, wireRecipientBankABA: e.target.value }))}
                  placeholder="9-digit ABA routing number"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Wire Recipient Bank Account Number *</label>
                <Input
                  type="password"
                  value={disbursementForm.wireRecipientBankAccount}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, wireRecipientBankAccount: e.target.value }))}
                  placeholder="Account number"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary-700 mb-1">Reference Number</label>
                <Input
                  value={disbursementForm.referenceNumber}
                  onChange={(e) => setDisbursementForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                  placeholder="Wire reference or memo (optional)"
                  className="border-orange-300 dark:border-orange-600 focus-visible:ring-orange-400"
                />
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const currentPhase = WIZARD_STEPS.find(s => s.id === currentStep)?.phase
  const phaseTheme = React.useMemo(() => {
    const brandFrom = 'from-primary'
    const brandTo = 'to-primary/80'
    const chip = 'bg-primary/10 text-primary'
    const ring = 'ring-primary/30'
    return { bg: `${brandFrom} ${brandTo}`, chip, ring }
  }, [currentPhase])

  // Section-level completion: all steps in allowedSteps must be complete
  const isSectionComplete = allowedSteps.every(s => isStepComplete(s))

  // Persistence (localStorage) and completion percent per section
  const storageKey = React.useMemo(() => {
    const exchangeId = currentExchange?.id || user?.id || 'anon'
    const scope = mode || 'all'
    return `sale_wizard_v1_${exchangeId}_${scope}`
  }, [currentExchange?.id, user?.id, mode])

  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const savedSnapshotRef = React.useRef<any | null>(null)
  const dbDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dbPendingPayloadRef = useRef<any | null>(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        // Only load saved data if it exists - don't pre-fill empty values
        // Phase One: Sale
        if (parsed.exchangerPhaseOne) {
          setExchangerInfoPhaseOne((prev) => ({
            ...prev,
            firstName: parsed.exchangerPhaseOne.firstName || prev.firstName,
            middleName: parsed.exchangerPhaseOne.middleName ?? prev.middleName,
            lastName: parsed.exchangerPhaseOne.lastName || prev.lastName,
            names: parsed.exchangerPhaseOne.names || prev.names,
            email: parsed.exchangerPhaseOne.email || prev.email || user?.email || '',
            phone: parsed.exchangerPhaseOne.phone || prev.phone || user?.phone || '',
            homeAddress: parsed.exchangerPhaseOne.homeAddress || prev.homeAddress,
            street: parsed.exchangerPhaseOne.street || prev.street,
            city: parsed.exchangerPhaseOne.city || prev.city,
            state: parsed.exchangerPhaseOne.state || prev.state,
            zipCode: parsed.exchangerPhaseOne.zipCode || prev.zipCode,
            taxIdNumber: parsed.exchangerPhaseOne.taxIdNumber || prev.taxIdNumber,
            beneficialInterest: parsed.exchangerPhaseOne.beneficialInterest || prev.beneficialInterest,
          }))
        }
        if (Array.isArray(parsed.exchangers) && parsed.exchangers.length > 0) {
          setExchangers(parsed.exchangers.map((ex: ExchangerInfo) => {
            if (ex.names && (!ex.firstName || !ex.lastName)) {
              const parts = ex.names.trim().split(/\s+/)
              const firstName = parts[0] || ''
              const lastName = parts.length > 1 ? parts[parts.length - 1] : ''
              const middleName = parts.length > 2 ? parts.slice(1, -1).join(' ') : ''
              return { ...ex, firstName, middleName, lastName }
            }
            return ex
          }))
        }
        if (!lockSalesHubPrefill) {
          if (parsed.buyer?.names) setBuyerInfo((prev) => ({ ...prev, names: parsed.buyer.names }))
          if (parsed.closingCompanySale?.name) setClosingCompanySale((prev) => ({ ...prev, ...parsed.closingCompanySale }))
          if (parsed.relinquished?.addressOrLegalDescription) setRelinquishedProperty((prev) => ({ ...prev, ...parsed.relinquished }))
        }
        if (parsed.identificationForm?.exchangerNames) setIdentificationForm((prev) => ({ ...prev, ...parsed.identificationForm }))
        // Phase Two: Purchases
        if (parsed.exchangerPhaseTwo?.names) setExchangerInfoPhaseTwo((prev) => ({ ...prev, ...parsed.exchangerPhaseTwo }))
        if (parsed.seller?.names) setSellerInfo((prev) => ({ ...prev, names: parsed.seller.names }))
        if (parsed.closingCompanyPurchase?.name) setClosingCompanyPurchase((prev) => ({ ...prev, ...parsed.closingCompanyPurchase }))
        if (parsed.replacement?.addressOrLegalDescription) setReplacementProperty((prev) => ({ ...prev, ...parsed.replacement }))
        if (parsed.disbursementForm?.exchangerNames) setDisbursementForm((prev) => ({ ...prev, ...parsed.disbursementForm }))
        if (typeof parsed.isInEscrow === 'boolean') setIsInEscrow(parsed.isInEscrow)
        if (typeof parsed.hasExtractedWithNeela === 'boolean') setHasExtractedWithNeela(parsed.hasExtractedWithNeela)
        if (typeof parsed.hasTitleReportExtracted === 'boolean') setHasTitleReportExtracted(parsed.hasTitleReportExtracted)
        if (typeof parsed.exchangeInfoConfirmed === 'boolean') setExchangeInfoConfirmed(parsed.exchangeInfoConfirmed)
        if (typeof parsed.uploadConfirmed === 'boolean') setUploadConfirmed(parsed.uploadConfirmed)
        if (typeof parsed.exchangersReviewed === 'boolean') setExchangersReviewed(parsed.exchangersReviewed)
        if (typeof parsed.propertyReviewed === 'boolean') setPropertyReviewed(parsed.propertyReviewed)
        // Load exchange info step questions - Base questions
        if (parsed.exchangeType) setExchangeType(normalizeExchangeType(parsed.exchangeType))
        if (parsed.propertySaleStatus) setPropertySaleStatus(parsed.propertySaleStatus)
        if (parsed.listPrice) setListPrice(parsed.listPrice)
        if (parsed.mortgagesPaidOff) setMortgagesPaidOff(parsed.mortgagesPaidOff)
        
        // Load Reverse Exchange questions
        if (parsed.reverseReplacementAcquired) setReverseReplacementAcquired(parsed.reverseReplacementAcquired)
        if (parsed.reverseHasTargetProperty) setReverseHasTargetProperty(parsed.reverseHasTargetProperty)
        if (parsed.reverseIntendedPurchaseDate) setReverseIntendedPurchaseDate(parsed.reverseIntendedPurchaseDate)
        if (parsed.reverseTargetPropertyAddress) setReverseTargetPropertyAddress(parsed.reverseTargetPropertyAddress)
        if (parsed.reverseTargetPropertyPrice) setReverseTargetPropertyPrice(parsed.reverseTargetPropertyPrice)
        if (parsed.reverseReplacementFunding) setReverseReplacementFunding(parsed.reverseReplacementFunding)
        if (parsed.reverseAcquisitionDate) setReverseAcquisitionDate(parsed.reverseAcquisitionDate)
        if (parsed.reverseReplacementAddress) setReverseReplacementAddress(parsed.reverseReplacementAddress)
        if (parsed.reverseReplacementSellerName) setReverseReplacementSellerName(parsed.reverseReplacementSellerName)
        if (parsed.reverseReplacementClosingCompany) setReverseReplacementClosingCompany(parsed.reverseReplacementClosingCompany)
        if (parsed.reverseReplacementClosingEmail) setReverseReplacementClosingEmail(parsed.reverseReplacementClosingEmail)
        if (parsed.reverseReplacementClosingPhone) setReverseReplacementClosingPhone(parsed.reverseReplacementClosingPhone)
        if (parsed.reverseReplacementFundingMethod) setReverseReplacementFundingMethod(parsed.reverseReplacementFundingMethod)
        if (parsed.reverseTitleHolder) setReverseTitleHolder(parsed.reverseTitleHolder)
        if (parsed.reverseRelinquishedAddress) setReverseRelinquishedAddress(parsed.reverseRelinquishedAddress)
        if (parsed.reverseRelinquishedDebt) setReverseRelinquishedDebt(parsed.reverseRelinquishedDebt)
        if (parsed.reverseRelinquishedClosingCompany) setReverseRelinquishedClosingCompany(parsed.reverseRelinquishedClosingCompany)
        if (parsed.reverseRelinquishedClosingEmail) setReverseRelinquishedClosingEmail(parsed.reverseRelinquishedClosingEmail)
        if (parsed.reverseRelinquishedClosingPhone) setReverseRelinquishedClosingPhone(parsed.reverseRelinquishedClosingPhone)
        if (parsed.reverseRelinquishedListed) setReverseRelinquishedListed(parsed.reverseRelinquishedListed)
        if (parsed.reverseEATAware) setReverseEATAware(parsed.reverseEATAware)
        if (parsed.reverse180Acknowledgment) setReverse180Acknowledgment(parsed.reverse180Acknowledgment)
        
        // Load Improvement/Construction Exchange questions
        if (parsed.improvementReplacementClosed) setImprovementReplacementClosed(parsed.improvementReplacementClosed)
        if (parsed.improvementIntendedCloseDate) setImprovementIntendedCloseDate(parsed.improvementIntendedCloseDate)
        if (parsed.improvementTargetAddress) setImprovementTargetAddress(parsed.improvementTargetAddress)
        if (parsed.improvementSalesAmount) setImprovementSalesAmount(parsed.improvementSalesAmount)
        if (parsed.improvementReplacementAddress) setImprovementReplacementAddress(parsed.improvementReplacementAddress)
        if (parsed.improvementSalePrice) setImprovementSalePrice(parsed.improvementSalePrice)
        if (parsed.improvementPropertyStatus) setImprovementPropertyStatus(parsed.improvementPropertyStatus)
        if (parsed.improvementConstructionScope) setImprovementConstructionScope(parsed.improvementConstructionScope)
        if (parsed.improvementConstructionBudget) setImprovementConstructionBudget(parsed.improvementConstructionBudget)
        if (parsed.improvement180DeadlineAware) setImprovement180DeadlineAware(parsed.improvement180DeadlineAware)
        if (parsed.improvementEATAware) setImprovementEATAware(parsed.improvementEATAware)
        if (parsed.improvementContractorSelected) setImprovementContractorSelected(parsed.improvementContractorSelected)
        if (parsed.improvementContractsSigned) setImprovementContractsSigned(parsed.improvementContractsSigned)
        if (parsed.currentStep && allowedSteps.includes(parsed.currentStep)) setCurrentStep(parsed.currentStep)
        setLastSavedAt(parsed.lastSavedAt || null)
        savedSnapshotRef.current = parsed
        if (Array.isArray(parsed.documents)) {
          setDocuments((prev) => prev.map(d => {
            const saved = parsed.documents.find((s: any) => s.name === d.name)
            if (saved) {
              let signedStatus = !!saved.signed
              let uploadedStatus = !!saved.uploaded
              if (d.name === 'Exchange Agreement') {
                signedStatus = signedStatus || localStorage.getItem('exchange_agreement_signed') === 'true'
                if (signedStatus) uploadedStatus = true
              } else if (d.name === 'W-9 Tax ID Certification') {
                signedStatus = signedStatus || localStorage.getItem('w9_signed') === 'true'
                if (signedStatus) uploadedStatus = true
              }
              return { ...d, uploaded: uploadedStatus, signed: signedStatus, file: null }
            }
            return d
          }))
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  // Ensure e-signed documents always reflect their localStorage status
  useEffect(() => {
    const checkSignedStatus = () => {
      const isAgreementSigned = localStorage.getItem('exchange_agreement_signed') === 'true'
      const isW9Signed = localStorage.getItem('w9_signed') === 'true'
      
      setDocuments(prev => prev.map(doc => {
        if (doc.name === 'Exchange Agreement' && isAgreementSigned && !doc.signed) {
          return { ...doc, uploaded: true, signed: true }
        }
        if (doc.name === 'W-9 Tax ID Certification' && isW9Signed && !doc.signed) {
          return { ...doc, uploaded: true, signed: true }
        }
        return doc
      }))
    }
    checkSignedStatus()
  }, [isSalesHubSubmitted, isReadOnly])

  // Update documents when exchangers are added/removed - create separate Exchange Agreement and W-9 for each additional exchanger
  useEffect(() => {
    if (!mode || mode !== 'phase1') return
    
    setDocuments(prev => {
      // Keep base documents and other non-exchanger-specific documents
      const baseDocs = prev.filter(d => 
        (d.name === 'Exchange Agreement' && !d.name.includes('(')) || 
        (d.name === 'W-9 Tax ID Certification' && !d.name.includes('(')) ||
        (!d.name.includes('Exchange Agreement') && !d.name.includes('W-9 Tax ID Certification'))
      )
      
      // Track existing exchanger-specific documents to preserve their signed status
      const existingExchangerDocs = new Map<string, DocumentUpload>()
      prev.forEach(doc => {
        if ((doc.name.includes('Exchange Agreement (') || doc.name.includes('W-9 Tax ID Certification (')) && doc.name.includes(')')) {
          existingExchangerDocs.set(doc.name, doc)
        }
      })
      
      // Add documents for each exchanger beyond the first
      const exchangerDocs: DocumentUpload[] = []
      exchangers.forEach((exchanger, index) => {
        if (index > 0) {
          const exchangerName = getExchangerFullName(exchanger) || `Exchanger ${index + 1}`
          const exchangeAgreementName = `Exchange Agreement (${exchangerName})`
          const w9Name = `W-9 Tax ID Certification (${exchangerName})`
          
          // Use existing document if it exists (to preserve signed status), otherwise create new
          const existingExchangeAgreement = existingExchangerDocs.get(exchangeAgreementName)
          const existingW9 = existingExchangerDocs.get(w9Name)
          
          exchangerDocs.push(
            existingExchangeAgreement || { name: exchangeAgreementName, file: null, uploaded: false, required: true, category: 'documents', signed: false },
            existingW9 || { name: w9Name, file: null, uploaded: false, required: true, category: 'irs', signed: false }
          )
        }
      })
      
      return [...baseDocs, ...exchangerDocs]
    })
  }, [exchangers.length, exchangers.map(e => e.names).join(','), mode])

  // Listen for sales hub completion events to update state
  useEffect(() => {
    const handleSalesHubCompleted = () => {
      setIsSalesHubSubmitted(true)
    }
    window.addEventListener('salesHubCompleted', handleSalesHubCompleted)
    return () => window.removeEventListener('salesHubCompleted', handleSalesHubCompleted)
  }, [])

  // Listen for identification hub completion events to update state
  useEffect(() => {
    const handleIdentificationHubCompleted = () => {
      setIsIdentificationHubSubmitted(true)
    }
    window.addEventListener('identificationHubCompleted', handleIdentificationHubCompleted)
    return () => window.removeEventListener('identificationHubCompleted', handleIdentificationHubCompleted)
  }, [])

  // Listen for purchase hub completion events to update state
  useEffect(() => {
    const handlePurchaseHubCompleted = () => {
      setIsPurchaseHubSubmitted(true)
    }
    window.addEventListener('purchaseHubCompleted', handlePurchaseHubCompleted)
    return () => window.removeEventListener('purchaseHubCompleted', handlePurchaseHubCompleted)
  }, [])

  // Listen for disbursement hub completion events to update state
  useEffect(() => {
    const handleDisbursementHubCompleted = () => {
      setIsDisbursementHubSubmitted(true)
    }
    window.addEventListener('disbursementHubCompleted', handleDisbursementHubCompleted)
    return () => window.removeEventListener('disbursementHubCompleted', handleDisbursementHubCompleted)
  }, [])

  // Listen for acquisition hub completion events to update state
  useEffect(() => {
    const handleAcquisitionHubCompleted = () => {
      setIsAcquisitionHubSubmitted(true)
    }
    window.addEventListener('acquisitionHubCompleted', handleAcquisitionHubCompleted)
    return () => window.removeEventListener('acquisitionHubCompleted', handleAcquisitionHubCompleted)
  }, [])

  // Periodically check localStorage to ensure read-only state persists
  useEffect(() => {
    const checkSalesHubSubmitted = () => {
      const submitted = localStorage.getItem('sales_hub_submitted') === 'true'
      if (submitted && !isSalesHubSubmitted) {
        setIsSalesHubSubmitted(true)
      } else if (!submitted && isSalesHubSubmitted) {
        setIsSalesHubSubmitted(false)
      }
    }
    
    const checkIdentificationHubSubmitted = () => {
      const submitted = localStorage.getItem('identification_hub_submitted') === 'true'
      if (submitted && !isIdentificationHubSubmitted) {
        setIsIdentificationHubSubmitted(true)
      } else if (!submitted && isIdentificationHubSubmitted) {
        setIsIdentificationHubSubmitted(false)
      }
    }
    
    const checkPurchaseHubSubmitted = () => {
      const submitted = localStorage.getItem('purchase_hub_submitted') === 'true'
      if (submitted && !isPurchaseHubSubmitted) {
        setIsPurchaseHubSubmitted(true)
      } else if (!submitted && isPurchaseHubSubmitted) {
        setIsPurchaseHubSubmitted(false)
      }
    }
    
    const checkDisbursementHubSubmitted = () => {
      const submitted = localStorage.getItem('disbursement_hub_submitted') === 'true'
      if (submitted && !isDisbursementHubSubmitted) {
        setIsDisbursementHubSubmitted(true)
      } else if (!submitted && isDisbursementHubSubmitted) {
        setIsDisbursementHubSubmitted(false)
      }
    }
    
    const checkAcquisitionHubSubmitted = () => {
      const submitted = localStorage.getItem('acquisition_hub_submitted') === 'true'
      if (submitted && !isAcquisitionHubSubmitted) {
        setIsAcquisitionHubSubmitted(true)
      } else if (!submitted && isAcquisitionHubSubmitted) {
        setIsAcquisitionHubSubmitted(false)
      }
    }
    
    checkSalesHubSubmitted()
    checkIdentificationHubSubmitted()
    checkPurchaseHubSubmitted()
    checkDisbursementHubSubmitted()
    checkAcquisitionHubSubmitted()
    const interval = setInterval(() => {
      checkSalesHubSubmitted()
      checkIdentificationHubSubmitted()
      checkPurchaseHubSubmitted()
      checkDisbursementHubSubmitted()
      checkAcquisitionHubSubmitted()
    }, 1000)
    
    return () => clearInterval(interval)
  }, [isSalesHubSubmitted, isIdentificationHubSubmitted, isPurchaseHubSubmitted, isDisbursementHubSubmitted, isAcquisitionHubSubmitted])

  // Set property sale date and log demo activities on mount
  useEffect(() => {
    if (isDemoUser && hasExtractedWithNeela && demoData?.property.closingDate) {
      const saleDate = new Date(demoData.property.closingDate).toISOString()
      localStorage.setItem('property_sale_date', saleDate)
      
      const hasLoggedDemo = localStorage.getItem('demo_activities_logged')
      if (!hasLoggedDemo) {
        const uploadTime = new Date()
        uploadTime.setHours(uploadTime.getHours() - 1)
        
        setTimeout(() => {
          activityLog.addActivity({
            type: 'document',
            title: 'Sales & Purchase Agreement Uploaded',
            description: 'Document successfully uploaded and processed',
            status: 'completed'
          })
          
          setTimeout(() => {
            activityLog.addActivity({
              type: 'document',
              title: 'Preliminary Title Report Uploaded',
              description: 'Document successfully uploaded and processed',
              status: 'completed'
            })
            
            localStorage.setItem('demo_activities_logged', 'true')
          }, 100)
        }, 100)
      }
    }
  }, [isDemoUser, hasExtractedWithNeela, demoData])

  // Reset current step when mode changes
  useEffect(() => {
    if (allowedSteps.length > 0 && !allowedSteps.includes(currentStep)) {
      setCurrentStep(allowedSteps[0])
    }
  }, [mode, allowedSteps, currentStep])

  // Lock background scroll only when rendered as a modal
  useEffect(() => {
    if (embedded) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [embedded])

  const sectionPercent = React.useMemo(() => {
    // Phase 1 mode: calculate based on step completion
    if (mode === 'phase1') {
      let completedCount = 0
      const bothDocsUploaded = !!salesPurchaseAgreement && !!preliminaryTitleReport
      
      // Exchange info step
      if (exchangeType && propertySaleStatus && listPrice && mortgagesPaidOff) completedCount++
      // Upload documents step
      if (!!salesPurchaseAgreement) completedCount++
      // Exchangers step
      if (bothDocsUploaded && exchangers.length > 0 && exchangers.every(e => e.firstName && e.lastName && e.email && e.phone && (e.homeAddress || (e.street && e.city && e.state && e.zipCode)) && e.taxIdNumber && e.beneficialInterest && parseFloat(e.beneficialInterest) > 0)) completedCount++
      // Property details step
      if (bothDocsUploaded && relinquishedProperty.addressOrLegalDescription && buyerInfo.names && closingCompanySale.name && closingCompanySale.address && closingCompanySale.phone && closingCompanySale.closerName && closingCompanySale.closerEmail) completedCount++
      // Signatures step - check all exchangers have signed
      const allExchangersSigned = exchangers.every((exchanger, index) => {
        const exchangerName = getExchangerFullName(exchanger) || `Exchanger ${index + 1}`
        const exchangeAgreementName = index === 0 
          ? 'Exchange Agreement' 
          : `Exchange Agreement (${exchangerName})`
        const w9Name = index === 0
          ? 'W-9 Tax ID Certification'
          : `W-9 Tax ID Certification (${exchangerName})`
        
        const exchangeAgreement = documents.find(d => d.name === exchangeAgreementName)
        const w9 = documents.find(d => d.name === w9Name)
        
        return !!(exchangeAgreement?.signed && w9?.signed)
      })
      if (allExchangersSigned) completedCount++
      return Math.round((completedCount / 5) * 100)
    }
    
    // Calculate based on completed steps only, not filled fields
    if (allowedSteps.length === 0) return 0
    
    let completedSteps = 0
    allowedSteps.forEach(step => {
      switch(step) {
        case 1:
          // For non-phase1 modes, check exchangerInfoPhaseOne
          if (exchangerInfoPhaseOne.names && exchangerInfoPhaseOne.email && exchangerInfoPhaseOne.phone && exchangerInfoPhaseOne.homeAddress && exchangerInfoPhaseOne.taxIdNumber) completedSteps++
          break
        case 2:
          if (buyerInfo.names) completedSteps++
          break
        case 3:
          if (closingCompanySale.name && closingCompanySale.address && closingCompanySale.phone && closingCompanySale.closerName && closingCompanySale.closerEmail) completedSteps++
          break
        case 4:
          if (relinquishedProperty.addressOrLegalDescription) completedSteps++
          break
        case 6:
          if (identificationForm.exchangerNames && identificationForm.relinquishedPropertyAddress && identificationForm.idRule && identificationForm.identifiedProperties.length > 0) completedSteps++
          break
        case 7:
          if (exchangerInfoPhaseTwo.names) completedSteps++
          break
        case 8:
          if (sellerInfo.names) completedSteps++
          break
        case 9:
          if (closingCompanyPurchase.name && closingCompanyPurchase.address && closingCompanyPurchase.closerName && closingCompanyPurchase.closerEmail) completedSteps++
          break
        case 10:
          if (replacementProperty.addressOrLegalDescription) completedSteps++
          break
        case 11:
          if (disbursementForm.exchangerNames && disbursementForm.amount && disbursementForm.wireRecipient && disbursementForm.wireRecipientAddress && disbursementForm.beneficiaryName && disbursementForm.wireRecipientBankName && disbursementForm.wireRecipientBankABA && disbursementForm.wireRecipientBankAccount) completedSteps++
          break
      }
    })
    
    return Math.round((completedSteps / allowedSteps.length) * 100)
  }, [
    allowedSteps, 
    mode, 
    exchangeType,
    propertySaleStatus,
    listPrice,
    mortgagesPaidOff,
    salesPurchaseAgreement,
    preliminaryTitleReport,
    isInEscrow,
    hasExtractedWithNeela,
    exchangers,
    exchangerInfoPhaseOne,
    buyerInfo,
    closingCompanySale,
    relinquishedProperty,
    identificationForm,
    exchangerInfoPhaseTwo,
    sellerInfo,
    closingCompanyPurchase,
    replacementProperty,
    disbursementForm,
    documents
  ])

  const saveProgressToStorage = React.useCallback(() => {
    try {
      const previousSnapshot = savedSnapshotRef.current
      
      // For Phase 1 mode, use the first exchanger from the exchangers array
      const exchangerData = mode === 'phase1' && exchangers.length > 0 
        ? exchangers[0] 
        : exchangerInfoPhaseOne
      
      const payload = {
        // Phase One: Sale
        exchangerPhaseOne: exchangerData,
        buyer: buyerInfo,
        closingCompanySale: closingCompanySale,
        relinquished: relinquishedProperty,
        identificationForm: identificationForm,
        // Exchange info step questions - use refs to get latest values without triggering auto-save
        exchangeType: exchangeTypeRef.current,
        propertySaleStatus: propertySaleStatusRef.current,
        listPrice: listPriceRef.current,
        mortgagesPaidOff: mortgagesPaidOffRef.current,
        // Reverse Exchange questions
        reverseReplacementAcquired: reverseReplacementAcquiredRef.current,
        reverseHasTargetProperty: reverseHasTargetPropertyRef.current,
        reverseIntendedPurchaseDate: reverseIntendedPurchaseDateRef.current,
        reverseTargetPropertyAddress: reverseTargetPropertyAddressRef.current,
        reverseTargetPropertyPrice: reverseTargetPropertyPriceRef.current,
        reverseReplacementFunding: reverseReplacementFundingRef.current,
        reverseAcquisitionDate: reverseAcquisitionDateRef.current,
        reverseReplacementAddress: reverseReplacementAddressRef.current,
        reverseReplacementSellerName: reverseReplacementSellerNameRef.current,
        reverseReplacementClosingCompany: reverseReplacementClosingCompanyRef.current,
        reverseReplacementClosingEmail: reverseReplacementClosingEmailRef.current,
        reverseReplacementClosingPhone: reverseReplacementClosingPhoneRef.current,
        reverseReplacementFundingMethod: reverseReplacementFundingMethodRef.current,
        reverseTitleHolder: reverseTitleHolderRef.current,
        reverseRelinquishedAddress: reverseRelinquishedAddressRef.current,
        reverseRelinquishedDebt: reverseRelinquishedDebtRef.current,
        reverseRelinquishedClosingCompany: reverseRelinquishedClosingCompanyRef.current,
        reverseRelinquishedClosingEmail: reverseRelinquishedClosingEmailRef.current,
        reverseRelinquishedClosingPhone: reverseRelinquishedClosingPhoneRef.current,
        reverseRelinquishedListed: reverseRelinquishedListedRef.current,
        reverseEATAware: reverseEATAwareRef.current,
        reverse180Acknowledgment: reverse180AcknowledgmentRef.current,
        // Improvement/Construction Exchange questions
        improvementReplacementClosed: improvementReplacementClosedRef.current,
        improvementIntendedCloseDate: improvementIntendedCloseDateRef.current,
        improvementTargetAddress: improvementTargetAddressRef.current,
        improvementSalesAmount: improvementSalesAmountRef.current,
        improvementReplacementAddress: improvementReplacementAddressRef.current,
        improvementSalePrice: improvementSalePriceRef.current,
        improvementPropertyStatus: improvementPropertyStatusRef.current,
        improvementConstructionScope: improvementConstructionScopeRef.current,
        improvementConstructionBudget: improvementConstructionBudgetRef.current,
        improvement180DeadlineAware: improvement180DeadlineAwareRef.current,
        improvementEATAware: improvementEATAwareRef.current,
        improvementContractorSelected: improvementContractorSelectedRef.current,
        improvementContractsSigned: improvementContractsSignedRef.current,
        // Phase Two: Purchases
        exchangerPhaseTwo: exchangerInfoPhaseTwo,
        seller: sellerInfo,
        closingCompanyPurchase: closingCompanyPurchase,
        replacement: replacementProperty,
        disbursementForm: disbursementForm,
        documents: documents.map(d => ({ name: d.name, uploaded: d.uploaded, required: d.required, category: d.category, signed: d.signed })),
        isInEscrow,
        salesPurchaseAgreementUploaded: !!salesPurchaseAgreement,
        preliminaryTitleReportUploaded: !!preliminaryTitleReport,
        hasExtractedWithNeela,
        saleExtractedData,
        hasTitleReportExtracted,
        titleExtractedData,
        exchangeInfoConfirmed,
        uploadConfirmed,
        exchangersReviewed,
        propertyReviewed,
        currentStep,
        lastSavedAt: new Date().toISOString(),
      }
      localStorage.setItem(storageKey, JSON.stringify(payload))
      setLastSavedAt(payload.lastSavedAt)
      savedSnapshotRef.current = payload

      if (SALE_WIZARD_USE_DB) {
        const realExchangeId = currentExchange?.id ?? null
        if (realExchangeId) {
          dbPendingPayloadRef.current = payload
          if (dbDebounceRef.current) {
            clearTimeout(dbDebounceRef.current)
          }
          dbDebounceRef.current = setTimeout(() => {
            const pendingPayload = dbPendingPayloadRef.current
            dbPendingPayloadRef.current = null
            wizardApi.saveHubData(realExchangeId, mode || 'all', pendingPayload)
              .then(() => {
                setLastSavedAt(pendingPayload.lastSavedAt)
              })
              .catch((err) => {
                console.error('SaleWizard: failed to persist to DB (localStorage save succeeded):', err)
              })
          }, SALE_WIZARD_DEBOUNCE_MS)
        }
      }

      // Note: NOT dispatching wizardDataUpdated here — that listener calls loadData
      // which re-renders this component, which re-runs the autosave effect, which
      // re-fires this save: an infinite request loop. Hub-submit handlers below
      // dispatch wizardDataUpdated explicitly when real downstream state changes.
      
      // Also save to data persistence
      const exchangerForPersistence = mode === 'phase1' && exchangers.length > 0 ? exchangers[0] : exchangerInfoPhaseOne
      if (exchangerForPersistence?.names) {
        const nameParts = exchangerForPersistence.names.split(' ')
        dataPersistence.merge({
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          ssn: exchangerForPersistence.taxIdNumber || ''
        })
      }
      if (relinquishedProperty?.addressOrLegalDescription) {
        dataPersistence.merge({
          propertyAddress: {
            street: relinquishedProperty.addressOrLegalDescription.split(',')[0] || '',
            city: relinquishedProperty.addressOrLegalDescription.split(',')[1]?.trim() || '',
            state: '',
            zipCode: ''
          }
        })
        if (!previousSnapshot?.relinquished?.addressOrLegalDescription || previousSnapshot.relinquished.addressOrLegalDescription !== relinquishedProperty.addressOrLegalDescription) {
          activityLog.logPropertyAdded(relinquishedProperty.addressOrLegalDescription, 'relinquished')
        }
      }
      if (relinquishedProperty?.closingDate) {
        localStorage.setItem('property_sale_date', new Date(relinquishedProperty.closingDate).toISOString())
      }
      if (closingCompanySale?.name) {
        dataPersistence.merge({
          closingCompanySale: {
            name: closingCompanySale.name,
            address: closingCompanySale.address,
            phone: closingCompanySale.phone,
            email: closingCompanySale.email,
            closerName: closingCompanySale.closerName,
            closerEmail: closingCompanySale.closerEmail
          }
        })
      }
      if (replacementProperty?.addressOrLegalDescription) {
        if (!previousSnapshot?.replacement?.addressOrLegalDescription || previousSnapshot.replacement.addressOrLegalDescription !== replacementProperty.addressOrLegalDescription) {
          activityLog.logPropertyAdded(replacementProperty.addressOrLegalDescription, 'replacement')
        }
      }
      const hasSignificantData = exchangerData?.names || buyerInfo?.names || relinquishedProperty?.addressOrLegalDescription || closingCompanySale?.name
      if (hasSignificantData && previousSnapshot) {
        activityLog.logSalesHubProgress('Sales Hub information saved')
      }
      
      return true
    } catch (error) {
      console.error('Failed to save progress:', error)
      return false
    }
  }, [storageKey, currentExchange, exchangers, exchangerInfoPhaseOne, buyerInfo, closingCompanySale, relinquishedProperty, identificationForm, exchangerInfoPhaseTwo, sellerInfo, closingCompanyPurchase, replacementProperty, disbursementForm, documents, isInEscrow, salesPurchaseAgreement, preliminaryTitleReport, hasExtractedWithNeela, saleExtractedData, hasTitleReportExtracted, titleExtractedData, exchangeInfoConfirmed, uploadConfirmed, exchangersReviewed, propertyReviewed, currentStep, mode, exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff, reverseReplacementAcquired, reverseHasTargetProperty, reverseIntendedPurchaseDate, reverseTargetPropertyAddress, reverseTargetPropertyPrice, reverseReplacementFunding, reverseAcquisitionDate, reverseReplacementAddress, reverseReplacementSellerName, reverseReplacementClosingCompany, reverseReplacementClosingEmail, reverseReplacementClosingPhone, reverseReplacementFundingMethod, reverseTitleHolder, reverseRelinquishedAddress, reverseRelinquishedDebt, reverseRelinquishedClosingCompany, reverseRelinquishedClosingEmail, reverseRelinquishedClosingPhone, reverseRelinquishedListed, reverseEATAware, reverse180Acknowledgment, improvementReplacementClosed, improvementIntendedCloseDate, improvementTargetAddress, improvementSalesAmount, improvementReplacementAddress, improvementSalePrice, improvementPropertyStatus, improvementConstructionScope, improvementConstructionBudget, improvement180DeadlineAware, improvementEATAware, improvementContractorSelected, improvementContractsSigned])

  useEffect(() => {
    return () => {
      if (dbDebounceRef.current) {
        clearTimeout(dbDebounceRef.current)
      }
      const realExchangeId = currentExchange?.id ?? null
      if (SALE_WIZARD_USE_DB && realExchangeId && dbPendingPayloadRef.current) {
        wizardApi.saveHubData(realExchangeId, mode || 'all', dbPendingPayloadRef.current).catch(() => {})
      }
    }
  }, [currentExchange, mode])

  // Auto-save on all field changes — skip the initial mount and any render
  // triggered solely by useHubData reloading from API (which would cause an
  // infinite save → reload loop).
  const didAutoSaveMountRef = useRef(false)
  useEffect(() => {
    if (!didAutoSaveMountRef.current) {
      didAutoSaveMountRef.current = true
      return
    }
    saveProgressToStorage()
  }, [saveProgressToStorage])
  useEffect(() => {
    if (mode !== 'phase1' || isReadOnly) return
    const nextStepMap: Record<Phase1Step, Phase1Step | null> = {
      'exchange-info': 'upload-documents',
      'upload-documents': 'exchangers',
      'exchangers': 'property-details',
      'property-details': 'signatures',
      'signatures': null
    }
    let isCurrentStepComplete = false
    if (phase1Step === 'exchange-info') {
      isCurrentStepComplete = !!(exchangeType && propertySaleStatus && listPrice && mortgagesPaidOff && exchangeInfoConfirmed)
    } else if (phase1Step === 'upload-documents') {
      const hasRequiredDoc = !!salesPurchaseAgreement
      const hasTitleReportIfNeeded = !isInEscrow || !!preliminaryTitleReport
      const documentsReadyForConfirm = hasRequiredDoc && hasTitleReportIfNeeded && hasExtractedWithNeela && (!preliminaryTitleReport || hasTitleReportExtracted)
      isCurrentStepComplete = documentsReadyForConfirm && uploadConfirmed
    } else if (phase1Step === 'exchangers') {
      const nonEmptyExchangers = exchangers.filter((e) => !!(e.firstName || e.lastName || e.email || e.phone))
      const exchangersBasicComplete = nonEmptyExchangers.length > 0 && nonEmptyExchangers.every((e) => {
        const hasAddress = !!(e.homeAddress || (e.street && e.city && e.state && e.zipCode))
        const beneficialInterest = e.beneficialInterest || (nonEmptyExchangers.length === 1 ? '100' : '')
        const hasClassification = !!(e.taxClassification && (e.taxClassification !== 'llc' || e.llcTaxClassification))
        return !!(e.firstName && e.lastName && e.email && e.phone && hasAddress && e.taxIdNumber && beneficialInterest && hasClassification)
      })
      isCurrentStepComplete = hasExtractedWithNeela && exchangersBasicComplete && exchangersReviewed
    } else if (phase1Step === 'property-details') {
      const hasBuyerInfo = !!(buyerInfo.names)
      const hasRelinquishedProperty = !!(relinquishedProperty.addressOrLegalDescription && relinquishedProperty.closingDate)
      const hasClosingCompany = !!(closingCompanySale.name && closingCompanySale.address)
      isCurrentStepComplete = hasBuyerInfo && hasRelinquishedProperty && hasClosingCompany && propertyReviewed
    } else if (phase1Step === 'signatures') {
      const exchangeAgreementDoc = documents.find(d => d.name === 'Exchange Agreement')
      const w9Doc = documents.find(d => d.name === 'W-9 Tax ID Certification')
      isCurrentStepComplete = !!(exchangeAgreementDoc?.signed && w9Doc?.signed)
    }
    const previousComplete = stepCompletionRef.current[phase1Step]
    if (previousComplete === undefined) {
      stepCompletionRef.current[phase1Step] = isCurrentStepComplete
      return
    }
    if (isCurrentStepComplete && !previousComplete) {
      stepCompletionRef.current[phase1Step] = true
      saveProgressToStorage()
      const nextStep = nextStepMap[phase1Step]
      if (nextStep) {
        onPhase1StepChange?.(nextStep)
      }
      return
    }
    if (!isCurrentStepComplete && previousComplete) {
      stepCompletionRef.current[phase1Step] = false
    }
  }, [mode, isReadOnly, phase1Step, exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff, exchangeInfoConfirmed, salesPurchaseAgreement, preliminaryTitleReport, hasExtractedWithNeela, hasTitleReportExtracted, uploadConfirmed, isInEscrow, exchangers, exchangersReviewed, propertyReviewed, buyerInfo, relinquishedProperty, closingCompanySale, documents, onPhase1StepChange, saveProgressToStorage])

  const goPrev = () => {
    if (mode === 'phase1') {
      const phase1Steps: Phase1Step[] = ['exchange-info', 'upload-documents', 'exchangers', 'property-details', 'signatures']
      const currentIdx = phase1Steps.indexOf(phase1Step)
      if (currentIdx > 0) {
        onPhase1StepChange?.(phase1Steps[currentIdx - 1])
      }
    } else {
      const idx = allowedSteps.indexOf(currentStep)
      setCurrentStep(allowedSteps[Math.max(0, idx - 1)])
    }
  }

  const goNext = () => {
    if (mode === 'phase1') {
      const phase1Steps: Phase1Step[] = ['exchange-info', 'upload-documents', 'exchangers', 'property-details', 'signatures']
      const currentIdx = phase1Steps.indexOf(phase1Step)
      if (currentIdx < phase1Steps.length - 1) {
        onPhase1StepChange?.(phase1Steps[currentIdx + 1])
      }
    } else {
      const idx = allowedSteps.indexOf(currentStep)
      setCurrentStep(allowedSteps[Math.min(allowedSteps.length - 1, idx + 1)])
    }
  }

  // Handle submit with validation and confirmation
  const handleSubmit = React.useCallback(async (identificationOverride?: IdentificationForm) => {
    console.log('handleSubmit called', { mode, isReadOnly, isSalesHubSubmitted, isIdentificationHubSubmitted })

    if (isReadOnly) {
      if (mode === 'phase1' && isSalesHubSubmitted) {
        toast.error('Sales Hub has already been submitted and is now read-only.')
      } else if (mode === 'exchange' && isIdentificationHubSubmitted) {
        toast.error('Identification Hub has already been submitted and is now read-only.')
      } else if (mode === 'acquisition' && (isPurchaseHubSubmitted || isAcquisitionHubSubmitted)) {
        toast.error('Purchase Hub has already been submitted and is now read-only.')
      } else if (mode === 'disbursement' && isDisbursementHubSubmitted) {
        toast.error('Disbursement Hub has already been submitted and is now read-only.')
      }
      return
    }

    if (mode === 'phase1') {
      if (!isDemoUser) {
        const step0Complete = isStepComplete(0)
        const step1Complete = isStepComplete(1)
        const step2Complete = isStepComplete(2)
        const step3Complete = isStepComplete(3)

        if (!step0Complete) {
          toast.error('Please complete the Upload Documents step before submitting')
          return
        }
        if (!step1Complete) {
          toast.error('Please complete all Exchanger Information fields before submitting')
          return
        }
        if (!step2Complete) {
          toast.error('Please complete all Property & Buyer Details fields before submitting')
          return
        }
        if (!step3Complete) {
          toast.error('Please sign both the Exchange Agreement and W-9 before submitting')
          return
        }
      }
    } else if (mode === 'exchange') {
      const formForSubmit = identificationOverride ?? identificationForm
      if (identificationOverride) {
        setIdentificationForm(identificationOverride)
      }
      if (!formForSubmit.exchangerNames || !formForSubmit.relinquishedPropertyAddress || !formForSubmit.idRule || formForSubmit.identifiedProperties.length === 0) {
        toast.error('Please complete all required fields in the Identification Form before submitting')
        return
      }
      if (!isDemoUser) {
        const totalFMV = formForSubmit.identifiedProperties.reduce(
          (sum: number, p: any) => sum + ((typeof p === 'string' ? 0 : p.estimatedFMV) ?? 0),
          0
        )
        const relinquishedFMV: number = (() => {
          try {
            const calcResults = JSON.parse(localStorage.getItem('calculator_results') || '{}')
            const fromCalc = calcResults?.input?.sellingPrice
            if (fromCalc && fromCalc > 0) return fromCalc as number
          } catch { /* ignore */ }
          return 0
        })()
        const cap = relinquishedFMV * 2
        if (formForSubmit.idRule === '3-property' && formForSubmit.identifiedProperties.length > 3) {
          toast.error('3-property rule allows up to 3 properties. Remove one or switch to the 200% rule.')
          return
        }
        if (formForSubmit.idRule === '200-percent' && cap > 0 && totalFMV > cap) {
          toast.error(
            `Total identified FMV ($${totalFMV.toLocaleString()}) exceeds 200% cap ($${cap.toLocaleString()}). Reduce allocations.`
          )
          return
        }
      }
    } else if (mode === 'acquisition') {
      // Purchase Hub validation - check if all required steps are complete
      const exchangerComplete = isStepComplete(7)
      const sellerComplete = isStepComplete(8)
      const closingCompanyComplete = isStepComplete(9)
      const propertyComplete = isStepComplete(10)
      
      if (!exchangerComplete || !sellerComplete || !closingCompanyComplete || !propertyComplete) {
        toast.error('Please complete all required fields in the Purchase Hub before submitting')
        return
      }
    } else if (mode === 'disbursement') {
      if (!disbursementForm.exchangerNames || !disbursementForm.amount || !disbursementForm.wireRecipient || !disbursementForm.wireRecipientAddress || !disbursementForm.beneficiaryName || !disbursementForm.wireRecipientBankName || !disbursementForm.wireRecipientBankABA || !disbursementForm.wireRecipientBankAccount) {
        toast.error('Please complete all required fields in the Disbursement Form before submitting')
        return
      }
    }
    
    const ok = await confirm({
      title: 'Submit Sales Hub?',
      description: 'Once submitted, this section will become read-only.',
      confirmText: 'Submit',
      destructive: false,
      icon: CheckCircle,
      iconColor: 'emerald',
    })
    if (!ok) return
    
    try {
      // Save all progress before submitting
      saveProgressToStorage()
      
      if (mode === 'phase1') {
        const submittedDate = new Date().toISOString()
        localStorage.setItem('sales_hub_submitted', 'true')
        localStorage.setItem('sales_hub_submitted_date', submittedDate)
        setIsSalesHubSubmitted(true)

        const numericListPrice = parseFloat(String(listPrice).replace(/[^0-9.]/g, '')) || 0
        const escrowData = createEscrowData(numericListPrice, 0)
        const escrowUserId = user?.id || 'anon'
        localStorage.setItem(`escrow_data_${escrowUserId}`, JSON.stringify(escrowData))
        window.dispatchEvent(new CustomEvent('escrowDataUpdated'))

        const exchangeId = currentExchange?.id || user?.id || 'anon'
        wizardApi.updateHubStatus(exchangeId, {
          hubSubmissions: { sales: { submitted: true, date: submittedDate } },
        }).catch(err => console.error('Failed to sync sales submission to DB:', err))

        activityLog.logSalesHubComplete()
        if (currentExchange?.id) {
          window.dispatchEvent(new CustomEvent('salesHubCompleted'))
          window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
        }

        toast.success('Sales Hub completed successfully! 🎉 The Identification Hub is now unlocked.')
      } else if (mode === 'exchange') {
        // Mark Identification Hub as submitted
        const submittedDate = new Date().toISOString()
        localStorage.setItem('identification_hub_submitted', 'true')
        localStorage.setItem('identification_hub_submitted_date', submittedDate)
        setIsIdentificationHubSubmitted(true)

        // Dual-write to DB
        const exchangeId = currentExchange?.id || user?.id || 'anon'
        wizardApi.updateHubStatus(exchangeId, {
          hubSubmissions: { identification: { submitted: true, date: submittedDate } },
        }).catch(err => console.error('Failed to sync identification submission to DB:', err))

        // Log activity and dispatch events
        activityLog.addActivity({
          type: 'hub',
          title: 'Identification Hub Submitted',
          description: 'Identification Hub has been submitted and is now in read-only mode.',
          status: 'completed'
        })
        window.dispatchEvent(new CustomEvent('identificationHubCompleted'))
        window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
        
        // Show success message
        toast.success('Identification Hub completed successfully! 🎉')
      } else if (mode === 'acquisition') {
        // Mark Purchase Hub as submitted
        const submittedDate = new Date().toISOString()
        localStorage.setItem('purchase_hub_submitted', 'true')
        localStorage.setItem('purchase_hub_submitted_date', submittedDate)
        localStorage.setItem('acquisition_hub_submitted', 'true')
        localStorage.setItem('acquisition_hub_submitted_date', submittedDate)
        setIsPurchaseHubSubmitted(true)
        setIsAcquisitionHubSubmitted(true)

        // Dual-write to DB
        const exchangeId = currentExchange?.id || user?.id || 'anon'
        wizardApi.updateHubStatus(exchangeId, {
          hubSubmissions: {
            purchase: { submitted: true, date: submittedDate },
            acquisition: { submitted: true, date: submittedDate },
          },
        }).catch(err => console.error('Failed to sync purchase submission to DB:', err))

        // Log activity and dispatch events
        activityLog.addActivity({
          type: 'hub',
          title: 'Purchase Hub Submitted',
          description: 'Purchase Hub has been submitted and is now in read-only mode.',
          status: 'completed'
        })
        window.dispatchEvent(new CustomEvent('purchaseHubCompleted'))
        window.dispatchEvent(new CustomEvent('acquisitionHubCompleted'))
        window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
        
        // Show success message
        toast.success('Purchase Hub completed successfully! 🎉')
      } else if (mode === 'disbursement') {
        // Mark Disbursement Hub as submitted
        const submittedDate = new Date().toISOString()
        localStorage.setItem('disbursement_hub_submitted', 'true')
        localStorage.setItem('disbursement_hub_submitted_date', submittedDate)
        setIsDisbursementHubSubmitted(true)

        // Dual-write to DB
        const exchangeId = currentExchange?.id || user?.id || 'anon'
        wizardApi.updateHubStatus(exchangeId, {
          hubSubmissions: { disbursement: { submitted: true, date: submittedDate } },
        }).catch(err => console.error('Failed to sync disbursement submission to DB:', err))

        // Log activity and dispatch events
        activityLog.addActivity({
          type: 'hub',
          title: 'Disbursement Hub Submitted',
          description: 'Disbursement Hub has been submitted and is now in read-only mode.',
          status: 'completed'
        })
        window.dispatchEvent(new CustomEvent('disbursementHubCompleted'))
        window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
        
        // Show success message
        toast.success('Disbursement Hub completed successfully! 🎉')
      }
      
      // Note: In phase1 mode, we don't call onComplete() because it's used for step completion
      // The Hub will handle the state update via the salesHubCompleted event listener
      // For other modes, call onComplete for backward compatibility
      if (mode !== 'phase1') {
        onComplete()
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error)
      toast.error('Failed to submit. Please try again.')
    }
  }, [saveProgressToStorage, onComplete, mode, isReadOnly, isSalesHubSubmitted, isIdentificationHubSubmitted, isPurchaseHubSubmitted, isDisbursementHubSubmitted, isAcquisitionHubSubmitted, identificationForm, disbursementForm, isStepComplete, exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff])

  // Expose simple control API to parent (Hub)
  useEffect(() => {
    if (!exposeControls) return
    ;(exposeControls as any)({
      goPrev,
      goNext,
      submit: handleSubmit,
      saveProgress: () => {
        const success = saveProgressToStorage()
        return success
      },
      canPrev: () => {
        if (mode === 'phase1') {
          return phase1Step !== 'exchange-info'
        }
        return currentStep !== allowedSteps[0]
      },
      canNext: () => {
        if (mode === 'phase1') {
          return phase1Step !== 'signatures'
        }
        return currentStep !== allowedSteps[allowedSteps.length - 1]
      },
      getPercent: () => sectionPercent,
      getLastSavedAt: () => lastSavedAt,
      isStepComplete: (step: number) => isStepComplete(step),
      getPhase1StepCompletion: (): Record<Phase1Step, boolean> => {
        if (mode !== 'phase1') {
          return {
            'exchange-info': false,
            'upload-documents': false,
            exchangers: false,
            'property-details': false,
            signatures: false
          }
        }
        const nonEmptyExchangers = exchangers.filter((e) => {
          return !!(
            e.firstName ||
            e.middleName ||
            e.lastName ||
            e.names ||
            e.email ||
            e.phone ||
            e.homeAddress ||
            e.street ||
            e.city ||
            e.state ||
            e.zipCode ||
            e.taxIdNumber ||
            e.beneficialInterest
          )
        })
        const exchangersBasicComplete = nonEmptyExchangers.length > 0 && nonEmptyExchangers.every((e) => {
          const hasAddress = !!(e.homeAddress || (e.street && e.city && e.state && e.zipCode))
          const beneficialInterest = e.beneficialInterest || (nonEmptyExchangers.length === 1 ? '100' : '')
          return !!(e.firstName && e.lastName && e.email && e.phone && hasAddress && e.taxIdNumber && beneficialInterest)
        })
        const exchangersReviewedComplete = !isDemoUser || !hasExtractedWithNeela || exchangersReviewed
        const propertyReviewedComplete = !isDemoUser || !hasExtractedWithNeela || propertyReviewed
        return {
          'exchange-info': !!(exchangeType && propertySaleStatus && listPrice && mortgagesPaidOff),
          'upload-documents': isStepComplete(0),
          exchangers: hasExtractedWithNeela && exchangersBasicComplete && exchangersReviewedComplete,
          'property-details': isStepComplete(2) && propertyReviewedComplete,
          signatures: isStepComplete(3)
        }
      },
      getEscrowState: () => ({
        isInEscrow,
        hasPreliminaryTitleReport: !!preliminaryTitleReport
      }),
      // Expose form field setters for chat auto-population
      setExchangerInfoPhaseOne,
      setBuyerInfo,
      setClosingCompanySale,
      setRelinquishedProperty,
      setIdentificationForm,
      setExchangerInfoPhaseTwo,
      setSellerInfo,
      setClosingCompanyPurchase,
      setReplacementProperty,
      setDisbursementForm,
      getCurrentStep: () => currentStep,
      setCurrentStep,
      // Expose getter for form data to check what's missing
      getFormData: () => ({
        exchangerPhaseOne: exchangerInfoPhaseOne,
        buyer: buyerInfo,
        closingCompanySale,
        relinquished: relinquishedProperty,
        identificationForm,
        exchangerPhaseTwo: exchangerInfoPhaseTwo,
        seller: sellerInfo,
        closingCompanyPurchase,
        replacement: replacementProperty,
        disbursementForm,
      }),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, allowedSteps, sectionPercent, mode, exchangers, relinquishedProperty, buyerInfo, closingCompanySale, documents, exchangeType, propertySaleStatus, listPrice, mortgagesPaidOff, phase1Step, hasExtractedWithNeela, hasTitleReportExtracted])

  const containerClass = embedded
    ? 'flex flex-col h-full'
    : 'bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col'

  const content = (
      <div className={containerClass}>
        {/* Header (hidden when embedded for seamless integration with hub tabs) */}
        {!embedded && (
          <div className={`bg-gradient-to-r ${phaseTheme.bg} text-white p-6`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Initial Setup Wizard</h2>
                <div className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${phaseTheme.chip} mt-2`}>{currentPhase}</div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>
        )}

        {/* Linear Progress - percentage of total fields completed */}
        {!embedded && (
          <div className="bg-secondary-50 px-6 py-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-secondary-700 font-medium">Section Progress</span>
                <span className="text-xs text-secondary-600">{sectionPercent}%</span>
              </div>
              <Progress value={sectionPercent} className="h-2" />
            </div>
          </div>
        )}

        {/* Content */}
        <div className={embedded ? 'flex-1 overflow-hidden p-6 min-h-0 flex flex-col' : 'flex-1 overflow-y-auto p-6'}>
          {isReadOnly && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-green-800 dark:text-green-200">
                    {mode === 'phase1' ? 'Sales Hub Completed' : 
                     mode === 'exchange' ? 'Identification Hub Completed' :
                     mode === 'acquisition' ? 'Purchase Hub Completed' :
                     mode === 'disbursement' ? 'Disbursement Hub Completed' :
                     'Section Completed'}
                  </h3>
                  <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                    {mode === 'phase1' ? 'This section has been submitted and is now read-only. The Identification Hub is now unlocked.' :
                     mode === 'exchange' ? 'This section has been submitted and is now read-only. The Purchase Hub is now unlocked.' :
                     mode === 'acquisition' ? 'This section has been submitted and is now read-only.' :
                     mode === 'disbursement' ? 'This section has been submitted and is now read-only.' :
                     'This section has been submitted and is now read-only.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          {renderStepContent()}
        </div>

        {/* Footer (optional when embedded under Hub) */}
        {!hideFooter && (
          <div className={embedded ? 'px-6 py-4 flex items-center justify-between' : 'border-t bg-secondary-50 px-6 py-4 flex items-center justify-between'}>
            {isReadOnly ? (
              <Button
                variant="outline"
                onClick={() => setShowTicketModal(true)}
                className="border-primary/30 text-primary hover:border-primary hover:bg-primary/10"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Request Edit
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={goPrev}
                disabled={currentStep === allowedSteps[0]}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
            )}
            
            <div className="text-sm text-secondary-600">{sectionPercent}% complete</div>

            <div className="flex items-center space-x-2">
              {lastSavedAt && !isReadOnly && (
                <div className="flex items-center space-x-2 mr-2">
                  <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse-scale"></span>
                  <span className="text-xs text-secondary-500">
                    Auto-saved {new Date(lastSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
              {!isReadOnly && (
                <Button
                  onClick={goNext}
                  disabled={currentStep === allowedSteps[allowedSteps.length - 1]}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
              {exchangeLoading && !currentExchange && !isReadOnly && (
                <span className="text-xs text-muted-foreground self-center">Loading your exchange...</span>
              )}
              <Button
                onClick={() => handleSubmit()}
                disabled={(!isSectionComplete && !isDemoUser) || isReadOnly || (exchangeLoading && !currentExchange)}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                {isReadOnly ? 'Submitted' : 'Submit'}
              </Button>
            </div>
          </div>
        )}
      </div>
  )

  const mainContent = (
    <>
      {content}
      {signingDoc && signingDocType && (
        <DocumentSigningModal
          documentName={signingDoc}
          documentType={signingDocType}
          exchangerData={{
            names: exchangers.length > 0 ? getExchangerFullName(exchangers[0]) : getExchangerFullName(exchangerInfoPhaseOne),
            homeAddress: exchangers.length > 0 ? exchangers[0].homeAddress : exchangerInfoPhaseOne.homeAddress,
            street: exchangers.length > 0 ? exchangers[0].street : exchangerInfoPhaseOne.street,
            city: exchangers.length > 0 ? exchangers[0].city : exchangerInfoPhaseOne.city,
            state: exchangers.length > 0 ? exchangers[0].state : exchangerInfoPhaseOne.state,
            zipCode: exchangers.length > 0 ? exchangers[0].zipCode : exchangerInfoPhaseOne.zipCode,
            taxIdNumber: exchangers.length > 0 ? exchangers[0].taxIdNumber : exchangerInfoPhaseOne.taxIdNumber,
            taxClassification: exchangers.length > 0 ? exchangers[0].taxClassification : exchangerInfoPhaseOne.taxClassification,
            llcTaxClassification: exchangers.length > 0 ? exchangers[0].llcTaxClassification : exchangerInfoPhaseOne.llcTaxClassification,
          }}
          buyerData={buyerInfo.names ? buyerInfo : undefined}
          closingCompanyData={closingCompanySale.name ? closingCompanySale : undefined}
          propertyData={relinquishedProperty.addressOrLegalDescription ? relinquishedProperty : undefined}
          bundleMeta={salesBundleMeta}
          escrowData={salesEscrowWireData}
          counterSignatures={DEMO1_COUNTER_SIGNATURES}
          onSign={(signature) => handleSignComplete(signingDoc, signature)}
          onClose={() => {
            setSigningDoc(null)
            setSigningDocType(null)
          }}
        />
      )}
      {viewingDoc && viewingDocType && (
        <DocumentSigningModal
          documentName={viewingDoc}
          documentType={viewingDocType}
          exchangerData={{
            names: exchangers.length > 0 ? getExchangerFullName(exchangers[0]) : getExchangerFullName(exchangerInfoPhaseOne),
            homeAddress: exchangers.length > 0 ? exchangers[0].homeAddress : exchangerInfoPhaseOne.homeAddress,
            street: exchangers.length > 0 ? exchangers[0].street : exchangerInfoPhaseOne.street,
            city: exchangers.length > 0 ? exchangers[0].city : exchangerInfoPhaseOne.city,
            state: exchangers.length > 0 ? exchangers[0].state : exchangerInfoPhaseOne.state,
            zipCode: exchangers.length > 0 ? exchangers[0].zipCode : exchangerInfoPhaseOne.zipCode,
            taxIdNumber: exchangers.length > 0 ? exchangers[0].taxIdNumber : exchangerInfoPhaseOne.taxIdNumber,
            taxClassification: exchangers.length > 0 ? exchangers[0].taxClassification : exchangerInfoPhaseOne.taxClassification,
            llcTaxClassification: exchangers.length > 0 ? exchangers[0].llcTaxClassification : exchangerInfoPhaseOne.llcTaxClassification,
          }}
          buyerData={buyerInfo.names ? buyerInfo : undefined}
          closingCompanyData={closingCompanySale.name ? closingCompanySale : undefined}
          propertyData={relinquishedProperty.addressOrLegalDescription ? relinquishedProperty : undefined}
          bundleMeta={salesBundleMeta}
          escrowData={salesEscrowWireData}
          counterSignatures={DEMO1_COUNTER_SIGNATURES}
          onSign={() => {}}
          onClose={() => {
            setViewingDoc(null)
            setViewingDocType(null)
          }}
          viewOnly={true}
        />
      )}

      {/* Edit Request Ticket Modal */}
      {showTicketModal && (
        <div className="modal-overlay modal-overlay-nested">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden">
            {/* Header */}
            <div className="border-b border-border px-6 py-4 flex items-center justify-between bg-gradient-to-r from-primary/10 to-blue-500/10">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/20 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">Request Edit</h3>
                  <p className="text-xs text-muted-foreground">Sales Hub Section</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowTicketModal(false)
                  setTicketMessage('')
                }} 
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-2">
                  What changes do you need?
                </label>
                <textarea
                  value={ticketMessage}
                  onChange={(e) => setTicketMessage(e.target.value)}
                  placeholder="Please describe the edits or corrections needed for your Sales Hub information..."
                  className="w-full px-4 py-3 border-2 border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-primary transition-all resize-none"
                  rows={6}
                  autoFocus
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">ℹ️</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                      What happens next?
                    </h4>
                    <p className="text-xs text-blue-800 dark:text-blue-200">
                      Our support team will review your request and contact you within 1-2 business days. You'll receive a confirmation email with your ticket number.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border px-6 py-4 bg-muted/50 flex items-center justify-between">
              <Button
                onClick={() => {
                  setShowTicketModal(false)
                  setTicketMessage('')
                }}
                variant="outline"
                className="border-border"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitTicket}
                disabled={!ticketMessage.trim()}
                className="bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-600 text-primary-foreground border-0 shadow-lg"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Submit Request
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )

  if (embedded) {
    return mainContent
  }

  const modal = (
    <div className="fixed inset-0 z-[9997] flex items-center justify-center p-4">{mainContent}</div>
  )

  if (typeof document !== 'undefined') {
    return createPortal(modal, document.body)
  }
  return modal
}


